import { Link, Navigate, Route, Routes, useLocation } from 'react-router';
import { Compass, ShieldAlert } from 'lucide-react';
import { lazy, Suspense, type ReactNode } from 'react';
import { Button, EmptyState, PageHeader, Spinner } from '@helios/ui';
import { PERMISSIONS, hasPermission } from '@helios/authz';
import { AppShell } from '@/components/layout/AppShell';
const CommandCenterPage = lazy(() => import('@/features/estimating/hub/CommandCenterPage').then((module) => ({ default: module.CommandCenterPage })));
const ProjectsPage = lazy(() => import('@/features/estimating/hub/ProjectsPage').then((module) => ({ default: module.ProjectsPage })));
const ProjectWorkspacePage = lazy(() => import('@/features/estimating/hub/ProjectWorkspacePage').then((module) => ({ default: module.ProjectWorkspacePage })));
const BidRegisterArea = lazy(() => import('@/features/estimating/intel/BidRegisterArea').then((module) => ({ default: module.BidRegisterArea })));
const IntelligenceArea = lazy(() => import('@/features/estimating/intel/IntelligenceArea').then((module) => ({ default: module.IntelligenceArea })));
const SettingsArea = lazy(() => import('@/features/estimating/intel/SettingsArea').then((module) => ({ default: module.SettingsArea })));
const CostBookEntry = lazy(() => import('@/features/estimating/costbook/CostBookEntry').then((module) => ({ default: module.CostBookEntry })));
const PortalRfqPage = lazy(() => import('@/features/portal/PortalRfqPage').then((module) => ({ default: module.PortalRfqPage })));
import { ExecutiveSummaryPage } from '@/features/command-center/CommandCenterPage';
import { ModuleScaffold } from '@/features/_scaffold/ModuleScaffold';
import { AuthPage } from '@/features/auth/AuthPage';
import { CompanySetupPage } from '@/features/company-setup/CompanySetupPage';
import { PasswordChangePage } from '@/features/auth/PasswordChangePage';
import { MicrosoftConnectionGate } from '@/features/auth/MicrosoftConnectionGate';
const IntegrationPrivacyPage = lazy(() =>
  import('@/features/integrations/IntegrationPrivacyPage').then((module) => ({
    default: module.IntegrationPrivacyPage,
  })),
);
import { BuildStatusPage } from '@/features/build-status/BuildStatusPage';
import { SeedDataCommandCenterPage } from '@/features/seed-data/SeedDataCommandCenterPage';
import { TestingCommandCenterPage } from '@/features/testing/TestingCommandCenterPage';
import { SecurityCommandCenterPage } from '@/features/security/SecurityCommandCenterPage';
import { InfraCommandCenterPage } from '@/features/infra/InfraCommandCenterPage';
import { ObservabilityCommandCenterPage } from '@/features/observability/ObservabilityCommandCenterPage';
import { LaunchCommandCenterPage } from '@/features/launch/LaunchCommandCenterPage';
import {
  SettingsIndex,
  SettingsShell,
  SettingsUnknownSection,
} from '@/features/settings/SettingsShell';
import { SettingsActivityPage } from '@/features/settings/SettingsActivityPage';
import { SettingsAuditPage } from '@/features/settings/SettingsAuditPage';
import { PlatformPurgePage } from '@/features/settings/PlatformPurgePage';
import { SettingsRolesPage } from '@/features/settings/SettingsRolesPage';
import { SettingsTenantsPage } from '@/features/settings/SettingsTenantsPage';
import { SettingsUsersPage } from '@/features/settings/SettingsUsersPage';
import { PreferencesSettingsPage } from '@/features/settings/PreferencesSettingsPage';
import { NotificationsSettingsPage } from '@/features/settings/NotificationsSettingsPage';
import { FeatureFlagsSettingsPage } from '@/features/settings/FeatureFlagsSettingsPage';
import { AiSettingsPage } from '@/features/settings/AiSettingsPage';
import { AccessSettingsPage } from '@/features/settings/AccessSettingsPage';
import { RetentionSettingsPage } from '@/features/settings/RetentionSettingsPage';
import { PlanSettingsPage } from '@/features/settings/PlanSettingsPage';
import { ProjectMapPage } from '@/features/realtime/ProjectMapPage';
import { useAuth } from '@/features/auth/authContext';
import { CLC_PORTAL_BASE, isClientLaunchPortalOnly } from '@/features/client-launch/clcPaths';
import {
  ALL_DESTINATIONS,
  LEGACY_SETTINGS_REDIRECTS,
  SCAFFOLD_DESTINATIONS,
  userCanAccessDestination,
  userCanSeeRailDestination,
  visibleInThisBuild,
} from '@/navigation/registry';
import type { NavDestination } from '@/types';

const UnifiedCrmPage = lazy(() =>
  import('@/pages/UnifiedCrmPage').then((module) => ({ default: module.UnifiedCrmPage })),
);

const ClientLaunchShell = lazy(() =>
  import('@/features/client-launch/ClientLaunchShell').then((module) => ({
    default: module.ClientLaunchShell,
  })),
);
const ClientLaunchOverviewPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchOverviewPage').then((module) => ({
    default: module.ClientLaunchOverviewPage,
  })),
);
const ClientLaunchReadinessPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchReadinessPage').then((module) => ({
    default: module.ClientLaunchReadinessPage,
  })),
);
const ClientLaunchOnboardingPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchOnboardingPage,
  })),
);
const ClientLaunchSystemsPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchSystemsPage,
  })),
);
const ClientLaunchVendorsPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchVendorsPage,
  })),
);
const ClientLaunchVendorDetailPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchVendorDetailPage,
  })),
);
const ClientLaunchSpendPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchSpendPage,
  })),
);
const ClientLaunchDocumentsPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchDocumentsPage,
  })),
);
const ClientLaunchIntegrationsPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchIntegrationsPage,
  })),
);
const ClientLaunchDecisionsPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchDecisionsPage,
  })),
);
const ClientLaunchTasksPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchTasksPage,
  })),
);
const ClientLaunchSupportPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchSupportPage,
  })),
);
const ClientLaunchControlRoomPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchControlRoomPage,
  })),
);
const ClientLaunchGuidePage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchGuidePage').then((module) => ({
    default: module.ClientLaunchGuidePage,
  })),
);
const ClientLaunchAttentionPage = lazy(() =>
  import('@/features/client-launch/pages/ClientLaunchPages').then((module) => ({
    default: module.ClientLaunchAttentionPage,
  })),
);
const ClientLaunchPortalShell = lazy(() =>
  import('@/features/client-launch/ClientLaunchPortalShell').then((module) => ({
    default: module.ClientLaunchPortalShell,
  })),
);
const ClientLaunchPortalLoginPage = lazy(() =>
  import('@/features/client-launch/ClientLaunchPortalLoginPage').then((module) => ({
    default: module.ClientLaunchPortalLoginPage,
  })),
);

const ProjectPortfolioPage = lazy(() =>
  import('@/features/projects/ProjectPortfolioPage').then((module) => ({
    default: module.ProjectPortfolioPage,
  })),
);
const ProjectOverviewPage = lazy(() =>
  import('@/features/projects/ProjectOverviewPage').then((module) => ({
    default: module.ProjectOverviewPage,
  })),
);
const ProjectSchedulePage = lazy(() =>
  import('@/features/scheduling/ProjectSchedulePage').then((module) => ({
    default: module.ProjectSchedulePage,
  })),
);
const ProjectBudgetPage = lazy(() =>
  import('@/features/finance/ProjectBudgetPage').then((module) => ({
    default: module.ProjectBudgetPage,
  })),
);
const ProjectControlsPage = lazy(() =>
  import('@/features/project-controls/ProjectControlsPage').then((module) => ({
    default: module.ProjectControlsPage,
  })),
);
const ClientAccountsPage = lazy(() =>
  import('@/features/unified-crm/clients/ClientAccountsPage').then((module) => ({
    default: module.ClientAccountsPage,
  })),
);
const LeadDetailPage = lazy(() =>
  import('@/features/estimating/LeadDetailPage').then((module) => ({
    default: module.LeadDetailPage,
  })),
);
const IntakePage = lazy(() =>
  import('@/features/estimating/IntakePage').then((module) => ({
    default: module.IntakePage,
  })),
);
const EquipmentPlanPage = lazy(() =>
  import('@/features/estimating/EquipmentPlanPage').then((module) => ({
    default: module.EquipmentPlanPage,
  })),
);
// P6 the estimating pipeline. Lazy for the same reason as M16-M18: the run
// screen pulls the whole stage tracker, the blocking list and the three bids.
const EstimatingPipelinePage = lazy(() =>
  import('@/features/estimating/EstimatingPipelinePage').then((module) => ({
    default: module.EstimatingPipelinePage,
  })),
);
const CostDatabasePage = lazy(() =>
  import('@/features/estimating/CostDatabasePage').then((module) => ({
    default: module.CostDatabasePage,
  })),
);
const EstimatesListPage = lazy(() =>
  import('@/features/estimating/EstimatesListPage').then((module) => ({
    default: module.EstimatesListPage,
  })),
);
const BidHistoryPage = lazy(() =>
  import('@/features/estimating/BidHistoryPage').then((module) => ({
    default: module.BidHistoryPage,
  })),
);
const BillingApplicationsPage = lazy(() =>
  import('@/features/billing/BillingApplicationsPage').then((module) => ({
    default: module.BillingApplicationsPage,
  })),
);
const CollectionsAgingPage = lazy(() =>
  import('@/features/billing/CollectionsAgingPage').then((module) => ({
    default: module.CollectionsAgingPage,
  })),
);
const JobCostExportPage = lazy(() =>
  import('@/features/finance/JobCostExportPage').then((module) => ({
    default: module.JobCostExportPage,
  })),
);
// M15. Lazy like every other feature page, which is what keeps recharts and the
// chart primitives out of the entry chunk.
const FinanceAnalyticsPage = lazy(() =>
  import('@/features/analytics/FinanceAnalyticsPage').then((module) => ({
    default: module.FinanceAnalyticsPage,
  })),
);
const ReportPage = lazy(() =>
  import('@/features/reporting/ReportPage').then((module) => ({
    default: module.ReportPage,
  })),
);
const AccountingMappingsPage = lazy(() =>
  import('@/features/accounting/AccountingMappingsPage').then((module) => ({
    default: module.AccountingMappingsPage,
  })),
);
const AccountingSyncPage = lazy(() =>
  import('@/features/accounting/AccountingSyncPage').then((module) => ({
    default: module.AccountingSyncPage,
  })),
);
// M14 banking. Lazy for the same reason the M15 analytics page is: the accounts
// screen pulls in the chart primitives, and they must not land in the entry
// chunk for every route.
const BankingWorkspacePage = lazy(() =>
  import('@/features/banking/BankingWorkspacePage').then((module) => ({
    default: module.BankingWorkspacePage,
  })),
);
const PlaidOAuthReturnPage = lazy(() =>
  import('@/features/banking/PlaidOAuthReturnPage').then((module) => ({
    default: module.PlaidOAuthReturnPage,
  })),
);
const UnifiedBankAccountsPage = lazy(() =>
  import('@/features/banking/UnifiedBankAccountsPage').then((module) => ({
    default: module.UnifiedBankAccountsPage,
  })),
);
const BankAccountsPage = lazy(() =>
  import('@/features/banking/BankAccountsPage').then((module) => ({
    default: module.BankAccountsPage,
  })),
);
const BankTransactionsPage = lazy(() =>
  import('@/features/banking/BankTransactionsPage').then((module) => ({
    default: module.BankTransactionsPage,
  })),
);
const BankSpendingPage = lazy(() =>
  import('@/features/banking/BankSpendingPage').then((module) => ({
    default: module.BankSpendingPage,
  })),
);
const BankStatementsPage = lazy(() =>
  import('@/features/banking/BankStatementsPage').then((module) => ({
    default: module.BankStatementsPage,
  })),
);
const QuickBooksReportsPage = lazy(() =>
  import('@/features/accounting/QuickBooksReportsPage').then((module) => ({
    default: module.QuickBooksReportsPage,
  })),
);
const AccountantWorkspacePage = lazy(() =>
  import('@/features/accounting/AccountantWorkspacePage').then((module) => ({
    default: module.AccountantWorkspacePage,
  })),
);
const FinanceTaxesPage = lazy(() =>
  import('@/features/finance/FinanceTaxesPage').then((module) => ({
    default: module.FinanceTaxesPage,
  })),
);
const IncomingBillsPage = lazy(() =>
  import('@/features/finance/incoming-bills/IncomingBillsPage').then((module) => ({
    default: module.IncomingBillsPage,
  })),
);

const BankReconciliationPage = lazy(() =>
  import('@/features/banking/BankReconciliationPage').then((module) => ({
    default: module.BankReconciliationPage,
  })),
);

// M16 takeoff. Lazy for the same reason as M14 and M15: the review screen pulls
// in the chart primitives, which must not land in the entry chunk.
const TakeoffPage = lazy(() =>
  import('@/features/takeoff/TakeoffPage').then((module) => ({ default: module.TakeoffPage })),
);
const TakeoffReviewPage = lazy(() =>
  import('@/features/takeoff/TakeoffReviewPage').then((module) => ({
    default: module.TakeoffReviewPage,
  })),
);
// M17 comping and labour. Lazy for the same reason as M16.
const CompingPage = lazy(() =>
  import('@/features/comping/CompingPage').then((module) => ({ default: module.CompingPage })),
);
const LaborPage = lazy(() =>
  import('@/features/labor/LaborPage').then((module) => ({ default: module.LaborPage })),
);
// M18 HR. Lazy like every other feature tree.
const HrWorkspacePage = lazy(() =>
  import('@/features/hr/HrWorkspacePage').then((module) => ({
    default: module.HrWorkspacePage,
  })),
);
const HrOverviewPage = lazy(() =>
  import('@/features/hr/HrOverviewPage').then((module) => ({
    default: module.HrOverviewPage,
  })),
);
const HrOnboardingPage = lazy(() =>
  import('@/features/hr/HrOnboardingPage').then((module) => ({
    default: module.HrOnboardingPage,
  })),
);
const NewHireOnboardingPage = lazy(() =>
  import('@/features/hr/NewHireOnboardingPage').then((module) => ({
    default: module.NewHireOnboardingPage,
  })),
);
const HrAnalyticsPage = lazy(() =>
  import('@/features/hr/HrAnalyticsPage').then((module) => ({
    default: module.HrAnalyticsPage,
  })),
);
const HrOrgChartPage = lazy(() =>
  import('@/features/hr/HrOrgChartPage').then((module) => ({
    default: module.HrOrgChartPage,
  })),
);
const HrCompliancePage = lazy(() =>
  import('@/features/hr/HrCompliancePage').then((module) => ({
    default: module.HrCompliancePage,
  })),
);
// P8 General Ledger — chart of accounts and journal entries.
const GeneralLedgerPage = lazy(() =>
  import('@/features/general-ledger/GeneralLedgerPage').then((module) => ({
    default: module.GeneralLedgerPage,
  })),
);
// P8 Legal Entities — the corporate/JV ownership structure.
const LegalEntitiesPage = lazy(() =>
  import('@/features/legal-entities/LegalEntitiesPage').then((module) => ({
    default: module.LegalEntitiesPage,
  })),
);
// P13 CRM depth and competitor intelligence.
const ClientContactDetailPage = lazy(() =>
  import('@/features/unified-crm/clients/ClientContactDetailPage').then((module) => ({
    default: module.ClientContactDetailPage,
  })),
);
const CompetitorSignalsPage = lazy(() =>
  import('@/features/competitor-intel/CompetitorSignalsPage').then((module) => ({
    default: module.CompetitorSignalsPage,
  })),
);
const CompetitorNewsPage = lazy(() =>
  import('@/features/competitor-intel/CompetitorNewsPage').then((module) => ({
    default: module.CompetitorNewsPage,
  })),
);
const CompetitorAlertsPage = lazy(() =>
  import('@/features/competitor-intel/CompetitorAlertsPage').then((module) => ({
    default: module.CompetitorAlertsPage,
  })),
);
// P12 Insurance — the company's own program.
const TechnologyWorkspacePage = lazy(() =>
  import('@/features/technology/TechnologyWorkspacePage').then((module) => ({
    default: module.TechnologyWorkspacePage,
  })),
);
const TechnologyOverviewPage = lazy(() =>
  import('@/features/technology/TechnologyOverviewPage').then((module) => ({
    default: module.TechnologyOverviewPage,
  })),
);
const TechnologyHardwarePage = lazy(() =>
  import('@/features/technology/TechnologyHardwarePage').then((module) => ({
    default: module.TechnologyHardwarePage,
  })),
);
const TechnologySoftwarePage = lazy(() =>
  import('@/features/technology/TechnologySoftwarePage').then((module) => ({
    default: module.TechnologySoftwarePage,
  })),
);
const TechnologyCamerasPage = lazy(() =>
  import('@/features/technology/TechnologyCamerasPage').then((module) => ({
    default: module.TechnologyCamerasPage,
  })),
);
const TechnologyDevicesPage = lazy(() =>
  import('@/features/technology/TechnologyDevicesPage').then((module) => ({
    default: module.TechnologyDevicesPage,
  })),
);
const TechnologyLicensesPage = lazy(() =>
  import('@/features/technology/TechnologyLicensesPage').then((module) => ({
    default: module.TechnologyLicensesPage,
  })),
);
const TechnologyDirectoryPage = lazy(() =>
  import('@/features/technology/TechnologyDirectoryPage').then((module) => ({
    default: module.TechnologyDirectoryPage,
  })),
);
const TechnologyNetworkPage = lazy(() =>
  import('@/features/technology/TechnologyNetworkPage').then((module) => ({
    default: module.TechnologyNetworkPage,
  })),
);
const TechnologyTenantConfigPage = lazy(() =>
  import('@/features/technology/TechnologyTenantConfigPage').then((module) => ({
    default: module.TechnologyTenantConfigPage,
  })),
);
const TechnologyImportPage = lazy(() =>
  import('@/features/technology/TechnologyImportPage').then((module) => ({
    default: module.TechnologyImportPage,
  })),
);
const InsuranceWorkspacePage = lazy(() =>
  import('@/features/insurance/InsuranceWorkspacePage').then((module) => ({
    default: module.InsuranceWorkspacePage,
  })),
);
const InsuranceOverviewPage = lazy(() =>
  import('@/features/insurance/InsuranceOverviewPage').then((module) => ({
    default: module.InsuranceOverviewPage,
  })),
);
const InsurancePoliciesPage = lazy(() =>
  import('@/features/insurance/InsurancePoliciesPage').then((module) => ({
    default: module.InsurancePoliciesPage,
  })),
);
const InsurancePolicyDetailPage = lazy(() =>
  import('@/features/insurance/InsurancePolicyDetailPage').then((module) => ({
    default: module.InsurancePolicyDetailPage,
  })),
);
const InsuranceClaimsPage = lazy(() =>
  import('@/features/insurance/InsuranceClaimsPage').then((module) => ({
    default: module.InsuranceClaimsPage,
  })),
);
const InsurancePremiumsPage = lazy(() =>
  import('@/features/insurance/InsurancePremiumsPage').then((module) => ({
    default: module.InsurancePremiumsPage,
  })),
);
const InsuranceCoveragePage = lazy(() =>
  import('@/features/insurance/InsuranceCoveragePage').then((module) => ({
    default: module.InsuranceCoveragePage,
  })),
);
const InsuranceCarriersPage = lazy(() =>
  import('@/features/insurance/InsuranceCarriersPage').then((module) => ({
    default: module.InsuranceCarriersPage,
  })),
);
const InsuranceDocumentsPage = lazy(() =>
  import('@/features/insurance/InsuranceDocumentsPage').then((module) => ({
    default: module.InsuranceDocumentsPage,
  })),
);
const PolicyAnalysisPage = lazy(() =>
  import('@/features/insurance/PolicyAnalysisPage').then((module) => ({
    default: module.PolicyAnalysisPage,
  })),
);
const InsuranceAlertsPage = lazy(() =>
  import('@/features/insurance/InsuranceAlertsPage').then((module) => ({
    default: module.InsuranceAlertsPage,
  })),
);
const InsuranceMarketPage = lazy(() =>
  import('@/features/insurance/InsuranceMarketPage').then((module) => ({
    default: module.InsuranceMarketPage,
  })),
);
const InsuranceProfilePage = lazy(() =>
  import('@/features/insurance/InsuranceProfilePage').then((module) => ({
    default: module.InsuranceProfilePage,
  })),
);
const PolicyIntakePage = lazy(() =>
  import('@/features/insurance/PolicyIntakePage').then((module) => ({
    default: module.PolicyIntakePage,
  })),
);
const PolicyAnalysisGapsPage = lazy(() =>
  import('@/features/insurance/PolicyAnalysisGapsPage').then((module) => ({
    default: module.PolicyAnalysisGapsPage,
  })),
);
// P15 Marketing — content, approval gate, Meta publishing, website feed.
const MarketingWorkspacePage = lazy(() =>
  import('@/features/marketing/MarketingWorkspacePage').then((module) => ({
    default: module.MarketingWorkspacePage,
  })),
);
const MarketingOverviewPage = lazy(() =>
  import('@/features/marketing/MarketingOverviewPage').then((module) => ({
    default: module.MarketingOverviewPage,
  })),
);
const MarketingContentPage = lazy(() =>
  import('@/features/marketing/MarketingContentPage').then((module) => ({
    default: module.MarketingContentPage,
  })),
);
const MarketingContentDetailPage = lazy(() =>
  import('@/features/marketing/MarketingContentDetailPage').then((module) => ({
    default: module.MarketingContentDetailPage,
  })),
);
const MarketingAutopilotPage = lazy(() =>
  import('@/features/marketing/MarketingAutopilotPage').then((module) => ({
    default: module.MarketingAutopilotPage,
  })),
);
const MarketingChannelsPage = lazy(() =>
  import('@/features/marketing/MarketingChannelsPage').then((module) => ({
    default: module.MarketingChannelsPage,
  })),
);
const MarketingEngagementPage = lazy(() =>
  import('@/features/marketing/MarketingEngagementPage').then((module) => ({
    default: module.MarketingEngagementPage,
  })),
);
const MarketingBrandStudioPage = lazy(() =>
  import('@/features/marketing/MarketingBrandStudioPage').then((module) => ({
    default: module.MarketingBrandStudioPage,
  })),
);
// P12 Sales — the business-development pipeline.
const SalesWorkspacePage = lazy(() =>
  import('@/features/sales/SalesWorkspacePage').then((module) => ({
    default: module.SalesWorkspacePage,
  })),
);
const SalesPipelinePage = lazy(() =>
  import('@/features/sales/SalesPipelinePage').then((module) => ({
    default: module.SalesPipelinePage,
  })),
);
const SalesProspectsPage = lazy(() =>
  import('@/features/sales/SalesProspectsPage').then((module) => ({
    default: module.SalesProspectsPage,
  })),
);
const SalesProspectDetailPage = lazy(() =>
  import('@/features/sales/SalesProspectDetailPage').then((module) => ({
    default: module.SalesProspectDetailPage,
  })),
);
const SalesActivityPage = lazy(() =>
  import('@/features/sales/SalesActivityPage').then((module) => ({
    default: module.SalesActivityPage,
  })),
);
const SalesReportsPage = lazy(() =>
  import('@/features/sales/SalesReportsPage').then((module) => ({
    default: module.SalesReportsPage,
  })),
);
const VendorsDirectoryPage = lazy(() =>
  import('@/features/unified-crm/vendors/VendorsDirectoryPage').then((module) => ({
    default: module.VendorsDirectoryPage,
  })),
);
const VendorsIngestPage = lazy(() =>
  import('@/features/unified-crm/vendors/VendorsIngestPage').then((module) => ({
    default: module.VendorsIngestPage,
  })),
);
// P12 Debt — the loan book.
const DebtOverviewPage = lazy(() =>
  import('@/features/debt/DebtOverviewPage').then((module) => ({
    default: module.DebtOverviewPage,
  })),
);
const CreditFacilitiesPage = lazy(() =>
  import('@/features/debt/CreditFacilitiesPage').then((module) => ({
    default: module.CreditFacilitiesPage,
  })),
);
const DebtDetailPage = lazy(() =>
  import('@/features/debt/DebtDetailPage').then((module) => ({
    default: module.DebtDetailPage,
  })),
);
// P8 Legal Entities — entity detail (owners, agreements, filings, tax, meetings, documents).
const LegalEntityDetailPage = lazy(() =>
  import('@/features/legal-entities/LegalEntityDetailPage').then((module) => ({
    default: module.LegalEntityDetailPage,
  })),
);
// P8 Legal — matters, counsel, invoices, calendar, risk, reports.
const LegalOverviewPage = lazy(() =>
  import('@/features/legal/LegalOverviewPage').then((module) => ({
    default: module.LegalOverviewPage,
  })),
);
const LegalMattersPage = lazy(() =>
  import('@/features/legal/LegalMattersPage').then((module) => ({
    default: module.LegalMattersPage,
  })),
);
const LegalMatterDetailPage = lazy(() =>
  import('@/features/legal/LegalMatterDetailPage').then((module) => ({
    default: module.LegalMatterDetailPage,
  })),
);
const LegalCounselPage = lazy(() =>
  import('@/features/legal/LegalCounselPage').then((module) => ({
    default: module.LegalCounselPage,
  })),
);
const LegalCounselDetailPage = lazy(() =>
  import('@/features/legal/LegalCounselDetailPage').then((module) => ({
    default: module.LegalCounselDetailPage,
  })),
);
const LegalInvoicesPage = lazy(() =>
  import('@/features/legal/LegalInvoicesPage').then((module) => ({
    default: module.LegalInvoicesPage,
  })),
);
const LegalCalendarPage = lazy(() =>
  import('@/features/legal/LegalCalendarPage').then((module) => ({
    default: module.LegalCalendarPage,
  })),
);
const LegalRiskPage = lazy(() =>
  import('@/features/legal/LegalRiskPage').then((module) => ({
    default: module.LegalRiskPage,
  })),
);
const LegalReportsPage = lazy(() =>
  import('@/features/legal/LegalReportsPage').then((module) => ({
    default: module.LegalReportsPage,
  })),
);
// P8 Benefits — its own nested workspace beside HR.
const BenefitsWorkspacePage = lazy(() =>
  import('@/features/benefits/BenefitsWorkspacePage').then((module) => ({
    default: module.BenefitsWorkspacePage,
  })),
);
const BenefitsOverviewPage = lazy(() =>
  import('@/features/benefits/BenefitsOverviewPage').then((module) => ({
    default: module.BenefitsOverviewPage,
  })),
);
const BenefitsPlansPage = lazy(() =>
  import('@/features/benefits/BenefitsPlansPage').then((module) => ({
    default: module.BenefitsPlansPage,
  })),
);
const BenefitsPlanDetailPage = lazy(() =>
  import('@/features/benefits/BenefitsPlanDetailPage').then((module) => ({
    default: module.BenefitsPlanDetailPage,
  })),
);
const BenefitsEnrollmentPage = lazy(() =>
  import('@/features/benefits/BenefitsEnrollmentPage').then((module) => ({
    default: module.BenefitsEnrollmentPage,
  })),
);
const BenefitsVendorsPage = lazy(() =>
  import('@/features/benefits/BenefitsVendorsPage').then((module) => ({
    default: module.BenefitsVendorsPage,
  })),
);
const BenefitsCostsPage = lazy(() =>
  import('@/features/benefits/BenefitsCostsPage').then((module) => ({
    default: module.BenefitsCostsPage,
  })),
);
const BenefitsClaimsPage = lazy(() =>
  import('@/features/benefits/BenefitsClaimsPage').then((module) => ({
    default: module.BenefitsClaimsPage,
  })),
);
const BenefitsCompliancePage = lazy(() =>
  import('@/features/benefits/BenefitsCompliancePage').then((module) => ({
    default: module.BenefitsCompliancePage,
  })),
);
const BenefitsDocumentsPage = lazy(() =>
  import('@/features/benefits/BenefitsDocumentsPage').then((module) => ({
    default: module.BenefitsDocumentsPage,
  })),
);
const BenefitsReportsPage = lazy(() =>
  import('@/features/benefits/BenefitsReportsPage').then((module) => ({
    default: module.BenefitsReportsPage,
  })),
);
// M18/P8 HR — employees, profile, compensation, time, performance, documents.
const HrEmployeesPage = lazy(() =>
  import('@/features/hr/HrEmployeesPage').then((module) => ({
    default: module.HrEmployeesPage,
  })),
);
const HrImportsPage = lazy(() =>
  import('@/features/hr/HrImportsPage').then((module) => ({
    default: module.HrImportsPage,
  })),
);
const HrEmployeeProfilePage = lazy(() =>
  import('@/features/hr/HrEmployeeProfilePage').then((module) => ({
    default: module.HrEmployeeProfilePage,
  })),
);
const HrCompensationPage = lazy(() =>
  import('@/features/hr/HrCompensationPage').then((module) => ({
    default: module.HrCompensationPage,
  })),
);
const HrTimePage = lazy(() =>
  import('@/features/hr/HrTimePage').then((module) => ({
    default: module.HrTimePage,
  })),
);
const HrPerformancePage = lazy(() =>
  import('@/features/hr/HrPerformancePage').then((module) => ({
    default: module.HrPerformancePage,
  })),
);
const HrDocumentsPage = lazy(() =>
  import('@/features/hr/HrDocumentsPage').then((module) => ({
    default: module.HrDocumentsPage,
  })),
);
// P8 Contracts — prime/subcontract administration, list and detail.
const ContractsListPage = lazy(() =>
  import('@/features/contracts/ContractsListPage').then((module) => ({
    default: module.ContractsListPage,
  })),
);
const ContractDetailPage = lazy(() =>
  import('@/features/contracts/ContractDetailPage').then((module) => ({
    default: module.ContractDetailPage,
  })),
);
// P9 Competitor Intel — real win/loss record against named competitors.
const CompetitorIntelPage = lazy(() =>
  import('@/features/competitor-intel/CompetitorIntelPage').then((module) => ({
    default: module.CompetitorIntelPage,
  })),
);
// M18 bid assembly and the counterparty register. Lazy for the same reason.
const BidAssemblyPage = lazy(() =>
  import('@/features/bidding/ReviewedBidAssemblyPage').then((module) => ({
    default: module.ReviewedBidAssemblyPage,
  })),
);
const CounterpartyHistoryPage = lazy(() =>
  import('@/features/bidding/CounterpartyHistoryPage').then((module) => ({
    default: module.CounterpartyHistoryPage,
  })),
);
const PayrollWorkspacePage = lazy(() =>
  import('@/features/payroll/PayrollWorkspacePage').then((module) => ({
    default: module.PayrollWorkspacePage,
  })),
);
const PayrollExportsPage = lazy(() =>
  import('@/features/payroll/PayrollExportsPage').then((module) => ({
    default: module.PayrollExportsPage,
  })),
);
const NativePayrollOAuthCallbackPage = lazy(() =>
  import('@/features/payroll/NativePayrollOAuthCallbackPage').then((module) => ({
    default: module.NativePayrollOAuthCallbackPage,
  })),
);
const PayrollHoursBoardPage = lazy(() =>
  import('@/features/payroll/PayrollHoursBoardPage').then((module) => ({
    default: module.PayrollHoursBoardPage,
  })),
);
const PayrollExportDetailPage = lazy(() =>
  import('@/features/payroll/PayrollExportDetailPage').then((module) => ({
    default: module.PayrollExportDetailPage,
  })),
);
const PayrollCostBoardPage = lazy(() =>
  import('@/features/payroll/PayrollCostBoardPage').then((module) => ({
    default: module.PayrollCostBoardPage,
  })),
);
// P5 prevailing wage and certified payroll. Lazy for the same reason as the
// M13 payroll screens: the filing detail carries a wide form table that no
// other route needs.
const CertifiedPayrollPage = lazy(() =>
  import('@/features/prevailing-wage/CertifiedPayrollPage').then((module) => ({
    default: module.CertifiedPayrollPage,
  })),
);
const CertifiedPayrollDetailPage = lazy(() =>
  import('@/features/prevailing-wage/CertifiedPayrollDetailPage').then((module) => ({
    default: module.CertifiedPayrollDetailPage,
  })),
);
const WageSchedulesPage = lazy(() =>
  import('@/features/prevailing-wage/WageSchedulesPage').then((module) => ({
    default: module.WageSchedulesPage,
  })),
);
const BillingApplicationDetailPage = lazy(() =>
  import('@/features/billing/BillingApplicationDetailPage').then((module) => ({
    default: module.BillingApplicationDetailPage,
  })),
);
const RetainageRegisterPage = lazy(() =>
  import('@/features/billing/RetainageRegisterPage').then((module) => ({
    default: module.RetainageRegisterPage,
  })),
);
const EstimateWorkspacePage = lazy(() =>
  import('@/features/estimating/EstimateWorkspacePage').then((module) => ({
    default: module.EstimateWorkspacePage,
  })),
);
const ClientAccountDetailPage = lazy(() =>
  import('@/features/unified-crm/clients/ClientAccountDetailPage').then((module) => ({
    default: module.ClientAccountDetailPage,
  })),
);
const FieldTodayPage = lazy(() =>
  import('@/features/field/FieldTodayPage').then((module) => ({
    default: module.FieldTodayPage,
  })),
);
const DailyReportCapturePage = lazy(() =>
  import('@/features/field/DailyReportCapturePage').then((module) => ({
    default: module.DailyReportCapturePage,
  })),
);
const DailyReportsPage = lazy(() =>
  import('@/features/field/DailyReportsPage').then((module) => ({
    default: module.DailyReportsPage,
  })),
);
const DispatchBoardPage = lazy(() =>
  import('@/features/field/DispatchBoardPage').then((module) => ({
    default: module.DispatchBoardPage,
  })),
);
const ObservationsPage = lazy(() =>
  import('@/features/field/ObservationsPage').then((module) => ({
    default: module.ObservationsPage,
  })),
);
const FieldTasksPage = lazy(() =>
  import('@/features/field/FieldTasksPage').then((module) => ({
    default: module.FieldTasksPage,
  })),
);
const FieldRfisPage = lazy(() =>
  import('@/features/field/FieldRfisPage').then((module) => ({
    default: module.FieldRfisPage,
  })),
);
const ComplianceBoardPage = lazy(() =>
  import('@/features/compliance/ComplianceBoardPage').then((module) => ({
    default: module.ComplianceBoardPage,
  })),
);
const InboxTriagePage = lazy(() =>
  import('@/features/inbox/InboxTriagePage').then((module) => ({
    default: module.InboxTriagePage,
  })),
);
const M365WorkspacePage = lazy(() =>
  import('@/features/m365-workspace/M365WorkspacePage').then((module) => ({
    default: module.M365WorkspacePage,
  })),
);
const M365OAuthCallbackPage = lazy(() =>
  import('@/features/m365-workspace/M365WorkspacePage').then((module) => ({
    default: module.M365OAuthCallbackPage,
  })),
);
const GoogleWorkspacePage = lazy(() =>
  import('@/features/google-workspace/GoogleWorkspacePage').then((module) => ({
    default: module.GoogleWorkspacePage,
  })),
);
const ThreadViewPage = lazy(() =>
  import('@/features/inbox/ThreadViewPage').then((module) => ({
    default: module.ThreadViewPage,
  })),
);
const DigitalTwinPage = lazy(() =>
  import('@/features/digital-twins/DigitalTwinPage').then((module) => ({
    default: module.DigitalTwinPage,
  })),
);
const SiteTelemetryPage = lazy(() =>
  import('@/features/site-telemetry/SiteTelemetryPage').then((module) => ({
    default: module.SiteTelemetryPage,
  })),
);
const AiAgentsPage = lazy(() =>
  import('@/features/ai-agents/AiAgentsPage').then((module) => ({
    default: module.AiAgentsPage,
  })),
);
const AiAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/AiAgentDetailPage').then((module) => ({
    default: module.AiAgentDetailPage,
  })),
);
const FinanceAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/FinanceAgentDetailPage').then((module) => ({
    default: module.FinanceAgentDetailPage,
  })),
);
const SchedulingAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/SchedulingAgentDetailPage').then((module) => ({
    default: module.SchedulingAgentDetailPage,
  })),
);
const ProcurementAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/ProcurementAgentDetailPage').then((module) => ({
    default: module.ProcurementAgentDetailPage,
  })),
);
const SafetyAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/SafetyAgentDetailPage').then((module) => ({
    default: module.SafetyAgentDetailPage,
  })),
);
const ComplianceAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/ComplianceAgentDetailPage').then((module) => ({
    default: module.ComplianceAgentDetailPage,
  })),
);
const EstimatingAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/EstimatingAgentDetailPage').then((module) => ({
    default: module.EstimatingAgentDetailPage,
  })),
);
const QaQcAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/QaQcAgentDetailPage').then((module) => ({
    default: module.QaQcAgentDetailPage,
  })),
);
const HrWorkforceAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/HrWorkforceAgentDetailPage').then((module) => ({
    default: module.HrWorkforceAgentDetailPage,
  })),
);
const EquipmentAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/EquipmentAgentDetailPage').then((module) => ({
    default: module.EquipmentAgentDetailPage,
  })),
);
const MarketIntelligenceAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/MarketIntelligenceAgentDetailPage').then((module) => ({
    default: module.MarketIntelligenceAgentDetailPage,
  })),
);
const GrowthMaAgentDetailPage = lazy(() =>
  import('@/features/ai-agents/GrowthMaAgentDetailPage').then((module) => ({
    default: module.GrowthMaAgentDetailPage,
  })),
);
const MarketIntelligenceCommandCenterPage = lazy(() =>
  import('@/features/market-intelligence/MarketIntelligenceCommandCenterPage').then((module) => ({
    default: module.MarketIntelligenceCommandCenterPage,
  })),
);
const GrowthMaCommandCenterPage = lazy(() =>
  import('@/features/growth-ma/GrowthMaCommandCenterPage').then((module) => ({
    default: module.GrowthMaCommandCenterPage,
  })),
);
const FinanceCommandCenterPage = lazy(() =>
  import('@/features/finance/FinanceCommandCenterPage').then((module) => ({
    default: module.FinanceCommandCenterPage,
  })),
);
const FinanceAreaEntry = lazy(() =>
  import('@/features/finance/FinanceAreaEntry').then((module) => ({
    default: module.FinanceAreaEntry,
  })),
);
const FinanceImportedSubjectsPage = lazy(() =>
  import('@/features/finance/FinanceImportedSubjectsPage').then((module) => ({
    default: module.FinanceImportedSubjectsPage,
  })),
);
const ProcurementCommandCenterPage = lazy(() =>
  import('@/features/procurement/ProcurementCommandCenterPage').then((module) => ({
    default: module.ProcurementCommandCenterPage,
  })),
);
const SchedulingCommandCenterPage = lazy(() =>
  import('@/features/scheduling/SchedulingCommandCenterPage').then((module) => ({
    default: module.SchedulingCommandCenterPage,
  })),
);
const SafetyCommandCenterPage = lazy(() =>
  import('@/features/safety/SafetyCommandCenterPage').then((module) => ({
    default: module.SafetyCommandCenterPage,
  })),
);
const QualityCommandCenterPage = lazy(() =>
  import('@/features/quality/QualityCommandCenterPage').then((module) => ({
    default: module.QualityCommandCenterPage,
  })),
);
const ApprovalsPage = lazy(() =>
  import('@/features/approvals/ApprovalsPage').then((module) => ({
    default: module.ApprovalsPage,
  })),
);
const TimeAutomationControlsPage = lazy(() =>
  import('@/features/time-automation/TimeAutomationControlsPage').then((module) => ({
    default: module.TimeAutomationControlsPage,
  })),
);
const MonitoringNoticePage = lazy(() =>
  import('@/features/time-automation/MonitoringNoticePage').then((module) => ({
    default: module.MonitoringNoticePage,
  })),
);
const ProvisioningSettingsPage = lazy(() =>
  import('@/features/time-automation/ProvisioningSettingsPage').then((module) => ({
    default: module.ProvisioningSettingsPage,
  })),
);
const FieldTimePage = lazy(() =>
  import('@/features/time-automation/FieldTimePage').then((module) => ({
    default: module.FieldTimePage,
  })),
);
const ClockSharkReconciliationPage = lazy(() =>
  import('@/features/time-automation/ClockSharkReconciliationPage').then((module) => ({
    default: module.ClockSharkReconciliationPage,
  })),
);
const FieldTimeSetupPage = lazy(() =>
  import('@/features/time-automation/FieldTimeSetupPage').then((module) => ({
    default: module.FieldTimeSetupPage,
  })),
);
const MyMonitoringNoticePage = lazy(() =>
  import('@/features/time-automation/MyMonitoringNoticePage').then((module) => ({
    default: module.MyMonitoringNoticePage,
  })),
);
const MyPunchesPage = lazy(() =>
  import('@/features/time-automation/MyPunchesPage').then((module) => ({
    default: module.MyPunchesPage,
  })),
);
const ResourcesCommandCenterPage = lazy(() =>
  import('@/features/resources/ResourcesCommandCenterPage').then((module) => ({
    default: module.ResourcesCommandCenterPage,
  })),
);
const LegacyDocumentLibraryRedirect = lazy(() =>
  import('@/features/documents/LegacyDocumentLibraryRedirect').then((module) => ({
    default: module.LegacyDocumentLibraryRedirect,
  })),
);
const DocumentsPage = lazy(() =>
  import('@/features/documents/DocumentsPage').then((module) => ({
    default: module.DocumentsPage,
  })),
);
const ReportingPage = lazy(() =>
  import('@/features/reports/ReportingPage').then((module) => ({
    default: module.ReportingPage,
  })),
);
const IntegrationsCommandCenterPage = lazy(() =>
  import('@/features/integrations/IntegrationsCommandCenterPage').then((module) => ({
    default: module.IntegrationsCommandCenterPage,
  })),
);
const SimulationsCommandCenterPage = lazy(() =>
  import('@/features/simulations/SimulationsCommandCenterPage').then((module) => ({
    default: module.SimulationsCommandCenterPage,
  })),
);
const LegacySearchRedirect = lazy(() =>
  import('@/features/search/LegacySearchRedirect').then((module) => ({
    default: module.LegacySearchRedirect,
  })),
);
// M11 STAFF selections authoring. The client-facing portal pages below are a
// separate tree entirely.
const SelectionsAuthoringPage = lazy(() =>
  import('@/features/portal-admin/SelectionsAuthoringPage').then((module) => ({
    default: module.SelectionsAuthoringPage,
  })),
);
const PortalAdministrationPage = lazy(() =>
  import('@/features/portal-admin/PortalAdministrationPage').then((module) => ({
    default: module.PortalAdministrationPage,
  })),
);
// M12 operations dashboard and the portfolio builder. `/` remains the
// executive command centre; the retired owner dashboard redirects there.
const OpsDashboardPage = lazy(() =>
  import('@/features/dashboards/OpsDashboardPage').then((module) => ({
    default: module.OpsDashboardPage,
  })),
);
const ShowcaseListPage = lazy(() =>
  import('@/features/portfolio/ShowcaseListPage').then((module) => ({
    default: module.ShowcaseListPage,
  })),
);
const ShowcaseBuilderPage = lazy(() =>
  import('@/features/portfolio/ShowcaseBuilderPage').then((module) => ({
    default: module.ShowcaseBuilderPage,
  })),
);
// ---------------------------------------------------------------------------
// M11 CLIENT PORTAL.
//
// Mounted OUTSIDE the authenticated `AppShell` tree, with its own minimal
// `PortalShell`. These pages authenticate with a portal session cookie, not a
// staff JWT, so they must never sit under `ProtectedShell` — a client has no
// staff session to protect, and a staff session grants nothing here.
// ---------------------------------------------------------------------------
const PortalShell = lazy(() =>
  import('@/features/portal/PortalShell').then((module) => ({ default: module.PortalShell })),
);
const PortalLoginPage = lazy(() =>
  import('@/features/portal/PortalLoginPage').then((module) => ({
    default: module.PortalLoginPage,
  })),
);
const PortalHomePage = lazy(() =>
  import('@/features/portal/PortalHomePage').then((module) => ({ default: module.PortalHomePage })),
);
const PortalProjectsPage = lazy(() =>
  import('@/features/portal/PortalProjectDirectoryPage').then((module) => ({
    default: module.PortalProjectsPage,
  })),
);
const PortalSharedDocumentsPage = lazy(() =>
  import('@/features/portal/PortalProjectDirectoryPage').then((module) => ({
    default: module.PortalSharedDocumentsPage,
  })),
);
const PortalConversationsPage = lazy(() =>
  import('@/features/portal/PortalProjectDirectoryPage').then((module) => ({
    default: module.PortalConversationsPage,
  })),
);
const PortalAiPage = lazy(() =>
  import('@/features/portal/PortalAiPage').then((module) => ({ default: module.PortalAiPage })),
);
const PortalGuidePage = lazy(() =>
  import('@/features/portal/PortalGuidePage').then((module) => ({
    default: module.PortalGuidePage,
  })),
);
const PortalCatalogPage = lazy(() =>
  import('@/features/portal/PortalCatalogPage').then((module) => ({
    default: module.PortalCatalogPage,
  })),
);
const PortalWorkPage = lazy(() =>
  import('@/features/portal/PortalWorkPage').then((module) => ({ default: module.PortalWorkPage })),
);
const PortalMoneyPage = lazy(() =>
  import('@/features/portal/PortalMoneyPage').then((module) => ({
    default: module.PortalMoneyPage,
  })),
);
const PortalCompliancePage = lazy(() =>
  import('@/features/portal/PortalCompliancePage').then((module) => ({
    default: module.PortalCompliancePage,
  })),
);
const PortalOnboardingPage = lazy(() =>
  import('@/features/portal/PortalOnboardingPage').then((module) => ({
    default: module.PortalOnboardingPage,
  })),
);
const PortalPhotosPage = lazy(() =>
  import('@/features/portal/PortalPhotosPage').then((module) => ({
    default: module.PortalPhotosPage,
  })),
);
const PortalParticipantGuard = lazy(() =>
  import('@/features/portal/PortalParticipantGuard').then((module) => ({
    default: module.PortalParticipantGuard,
  })),
);
const PortalStatusPage = lazy(() =>
  import('@/features/portal/PortalStatusPage').then((module) => ({
    default: module.PortalStatusPage,
  })),
);
const PortalSelectionsPage = lazy(() =>
  import('@/features/portal/PortalSelectionsPage').then((module) => ({
    default: module.PortalSelectionsPage,
  })),
);
const PortalDocumentsPage = lazy(() =>
  import('@/features/portal/PortalDocumentsPage').then((module) => ({
    default: module.PortalDocumentsPage,
  })),
);
const PortalMessagesPage = lazy(() =>
  import('@/features/portal/PortalMessagesPage').then((module) => ({
    default: module.PortalMessagesPage,
  })),
);
const PortalDecisionLogPage = lazy(() =>
  import('@/features/portal/PortalDecisionLogPage').then((module) => ({
    default: module.PortalDecisionLogPage,
  })),
);

function SessionLoadingPage() {
  return (
    <main className="bg-background text-foreground flex min-h-screen items-center justify-center">
      <div className="border-border bg-card text-muted-foreground flex items-center gap-3 rounded-lg border px-4 py-3 text-sm">
        <Spinner className="text-primary h-4 w-4" aria-hidden="true" />
        Resolving secure session…
      </div>
    </main>
  );
}

function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="text-muted-foreground flex min-h-[360px] items-center justify-center text-sm">
          <Spinner className="text-primary mr-2 h-4 w-4" aria-hidden="true" />
          Loading project workspace…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function ProtectedShell() {
  const auth = useAuth();
  const location = useLocation();

  if (auth.isLoading) {
    return <SessionLoadingPage />;
  }

  if (!auth.authenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  if (auth.accessMode === 'PASSWORD_CHANGE_REQUIRED') {
    return <Navigate to="/auth/password-change" replace state={{ from: location }} />;
  }

  if (auth.accessMode === 'COMPANY_SETUP') {
    return <Navigate to="/company-setup" replace />;
  }

  // Launch-portal principals never enter the ConstructionOS staff shell.
  if (isClientLaunchPortalOnly(auth.permissions)) {
    return <Navigate to={CLC_PORTAL_BASE} replace />;
  }

  return (
    <MicrosoftConnectionGate>
      <AppShell />
    </MicrosoftConnectionGate>
  );
}

function PermissionDeniedPage({ destination }: { destination: NavDestination }) {
  return (
    <EmptyState
      icon={<ShieldAlert className="h-5 w-5" aria-hidden="true" />}
      title="Permission required"
      description={`Your current role does not include ${destination.permission ?? 'the required permission'} for ${destination.label}.`}
      action={
        <Link
          to="/"
          className="text-primary inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
        >
          Back to Executive Overview
        </Link>
      }
    />
  );
}

function ProtectedDestination({ destination }: { destination: NavDestination }) {
  const auth = useAuth();
  const hasAccess = userCanAccessDestination(destination, auth.permissions);

  if (!hasAccess) {
    return <PermissionDeniedPage destination={destination} />;
  }
  return <ModuleScaffold destination={destination} />;
}

function ProtectedLiveDestination({
  destination,
  children,
}: {
  destination: NavDestination;
  children: ReactNode;
}) {
  const auth = useAuth();
  if (!userCanAccessDestination(destination, auth.permissions)) {
    return <PermissionDeniedPage destination={destination} />;
  }
  return children;
}

/**
 * Workspace layout chrome (HR / Payroll / Accounting). Sibling-tab grants may
 * enter the hub shell; they must not open the parent destination’s own page.
 */
function ProtectedHubLayout({
  destination,
  children,
}: {
  destination: NavDestination;
  children: ReactNode;
}) {
  const auth = useAuth();
  if (!userCanSeeRailDestination(destination, auth.permissions)) {
    return <PermissionDeniedPage destination={destination} />;
  }
  return children;
}

/**
 * Gate a route on a bare permission key rather than on a navigation destination.
 *
 * Used by P4, whose sidebar entry is a separate change owned elsewhere: a route
 * conditioned on `ALL_DESTINATIONS.find(...)` silently renders nothing when the
 * registry entry is absent, which is a dead route rather than a missing menu
 * item. The page is reachable by URL and from the lead funnel either way.
 */
/** Portal administration opens for any role that can see one of its workspaces. */
const PORTAL_ADMIN_ANY_PERMISSIONS = [
  PERMISSIONS.PORTAL_CONTACTS_READ,
  PERMISSIONS.PORTAL_MESSAGES_READ,
  PERMISSIONS.PORTAL_DOCUMENT_SHARES_READ,
  'finance:ap_invoices:read',
  PERMISSIONS.INTEGRATIONS_CONNECTIONS_READ,
];

function ProtectedAnyPermission({
  permissions,
  children,
}: {
  permissions: readonly string[];
  children: ReactNode;
}) {
  const auth = useAuth();
  if (!permissions.some((permission) => hasPermission(auth.permissions, permission))) {
    return (
      <EmptyState
        icon={<ShieldAlert className="h-5 w-5" aria-hidden="true" />}
        title="Permission required"
        description="Your current role does not include portal administration access."
        action={
          <Link
            to="/"
            className="text-primary inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
          >
            Back to Executive Overview
          </Link>
        }
      />
    );
  }
  return <>{children}</>;
}

function ProtectedPermission({
  permission,
  children,
}: {
  permission: string;
  children: ReactNode;
}) {
  const auth = useAuth();
  if (!hasPermission(auth.permissions, permission)) {
    return (
      <EmptyState
        icon={<ShieldAlert className="h-5 w-5" aria-hidden="true" />}
        title="Permission required"
        description={`Your current role does not include ${permission}.`}
        action={
          <Link
            to="/"
            className="text-primary inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
          >
            Back to Executive Overview
          </Link>
        }
      />
    );
  }
  return children;
}

function LegacyExecutiveSummaryRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search, hash: location.hash }} replace />;
}

/**
 * A module that moved under a new prefix. Keeps the remaining path segments,
 * query string and hash so record links (/clients/:id, /vendors?vendor=)
 * stored in notifications, emails and bookmarks still open the same record.
 */
function RelocatedPrefixRedirect({ from, to }: { from: string; to: string }) {
  const location = useLocation();
  const rest = location.pathname.startsWith(from) ? location.pathname.slice(from.length) : '';
  return (
    <Navigate
      to={{ pathname: `${to}${rest}`, search: location.search, hash: location.hash }}
      replace
    />
  );
}

/** The former root register anchor now belongs to the dedicated facility ledger. */
function LegacyDebtRedirect() {
  const location = useLocation();
  const rest = location.pathname.startsWith('/debt') ? location.pathname.slice('/debt'.length) : '';
  const debtRoot = location.pathname === '/debt' || location.pathname === '/debt/';
  const registerBookmark = debtRoot && location.hash === '#debt-records';
  return (
    <Navigate
      to={{
        pathname: registerBookmark ? '/credit-facilities/ledger' : `/credit-facilities${rest}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function ClientLaunchStaffIndex() {
  const auth = useAuth();
  if (hasPermission(auth.permissions, PERMISSIONS.CLIENT_LAUNCH_MANAGE)) {
    return <Navigate to="/client-launch/readiness" replace />;
  }
  return (
    <LazyRoute>
      <ClientLaunchOverviewPage />
    </LazyRoute>
  );
}

function NotFoundPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="404"
        title="Page not found"
        description="This route does not exist in HELIOS ConstructionOS."
      />
      <Button asChild>
        <Link to="/">
          <Compass className="h-4 w-4" aria-hidden="true" />
          Back to Executive Overview
        </Link>
      </Button>
    </div>
  );
}

/**
 * Application routes. Executive Overview is the home page; every other destination
 * from the navigation registry is reachable and renders a real ModuleScaffold —
 * there are no dead routes.
 */
export function AppRoutes() {
  const buildStatusDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'build-status',
  );
  const seedDataDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'seed-data',
  );
  const testingDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'testing');
  const securityDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'security');
  const infraDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'infra');
  const observabilityDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'observability',
  );
  const launchDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'launch');
  const clientLaunchDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'client-launch',
  );
  const projectMapDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'project-map',
  );
  const executiveSummaryDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'executive-summary',
  );
  const m365WorkspaceDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'm365-workspace',
  );
  const googleWorkspaceDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'google-workspace',
  );
  const projectPortfolioDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'project-portfolio',
  );
  const projectOverviewDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'project-overview',
  );
  const projectScheduleDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'project-schedule',
  );
  const projectBudgetDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'project-budget',
  );
  const rfiDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'rfis');
  const submittalDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'submittals',
  );
  const changeOrderDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'change-orders',
  );
  const clientAccountsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'crm-client-accounts',
  );
  const unifiedCrmDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'unified-crm',
  );
  const leadFunnelDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'lead-funnel',
  );
  const bidIntakeDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bid-intake',
  );
  const estimatingEquipmentDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'estimating-equipment',
  );
  const estimatingConfigDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'estimating-config',
  );
  const costDatabaseDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'cost-database',
  );
  const estimatesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'estimates',
  );
  const digitalTwinDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'digital-twin',
  );
  const siteTelemetryDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'site-telemetry',
  );
  const aiAgentsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'ai-agents',
  );
  const agentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'agent-detail',
  );
  const financeAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'finance-agent-detail',
  );
  const schedulingAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'scheduling-agent-detail',
  );
  const procurementAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'procurement-agent-detail',
  );
  const safetyAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'safety-agent-detail',
  );
  const complianceAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'compliance-agent-detail',
  );
  const bidHistoryDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bid-history',
  );
  // The pay-application detail screen is a child of the list and shares its
  // destination, exactly as the estimate workspace shares the estimates one.
  const billingApplicationsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'billing-applications',
  );
  const billingCollectionsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'billing-collections',
  );
  const retainageRegisterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'retainage-register',
  );
  // M13 accounting and payroll. The payroll detail screen is a child of the
  // list and shares its destination, exactly as the pay applications do.
  const jobCostExportDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'job-cost-export',
  );
  // M15 analytics and visualization.
  const financeAnalyticsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'finance-analytics',
  );
  const quickBooksReportsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'quickbooks-reports',
  );
  const accountantWorkspaceDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'accounting-workspace',
  );
  const financeTaxesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'finance-taxes',
  );
  const incomingBillsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'incoming-bills',
  );
  const bankSpendingDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bank-spending',
  );
  const bankStatementsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bank-statements',
  );
  const financeSubjectsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'finance-subjects',
  );
  const accountingMappingsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'accounting-mappings',
  );
  const accountingSyncDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'accounting-sync',
  );
  // M14 banking. Register and reconciliation stay as real child routes (and
  // catalog entries) so deep links and per-surface RBAC do not collapse. The
  // sidebar shows one Bank Accounts item; tabs switch between the three views.
  const bankAccountsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bank-accounts',
  );
  const bankTransactionsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bank-transactions',
  );
  const bankReconciliationDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bank-reconciliation',
  );

  // M16 takeoff. The review screen is a child route rather than its own
  // destination: it is always reached from a run, and a sidebar entry that
  // needed a run id to be useful would be a dead link most of the time.
  const takeoffDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'takeoff');
  // M17: the two stages between the punch list and the bid.
  const compingDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'comping');
  const laborDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'labor-plan');
  const hrDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'hr');
  const benefitsDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'benefits');
  const generalLedgerDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'general-ledger',
  );
  const legalEntitiesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-entities',
  );
  const contractsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'contracts',
  );
  const competitorsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'competitors',
  );
  const legalOverviewDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-overview',
  );
  const itEstateDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-estate',
  );
  const itHardwareDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-hardware',
  );
  const itSoftwareDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-software',
  );
  const itCamerasDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-cameras',
  );
  const itDevicesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-devices',
  );
  const itLicensesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-licenses',
  );
  const itDirectoryDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-directory',
  );
  const itNetworkDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-network',
  );
  const itTenantConfigDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-tenant-config',
  );
  const itImportDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'it-import',
  );
  const insuranceDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance',
  );
  const insurancePoliciesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-policies',
  );
  const insuranceClaimsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-claims',
  );
  const insurancePremiumsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-premiums',
  );
  const insuranceCoverageDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-coverage',
  );
  const insuranceCarriersDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-carriers',
  );
  const insuranceDocumentsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-documents',
  );
  const insuranceAlertsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-alerts',
  );
  const insuranceProfileDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-profile',
  );
  const insuranceIntakeDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-intake',
  );
  const insuranceMarketDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-market',
  );
  const insuranceCoverageGapsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'insurance-coverage-gaps',
  );
  const marketingDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'marketing',
  );
  const marketingContentDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'marketing-content',
  );
  const marketingAutopilotDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'marketing-autopilot',
  );
  const marketingChannelsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'marketing-channels',
  );
  const marketingEngagementDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'marketing-engagement',
  );
  const marketingBrandStudioDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'marketing-brand-studio',
  );
  const salesPipelineDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'sales-pipeline',
  );
  const salesProspectsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'sales-prospects',
  );
  const salesActivityDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'sales-activity',
  );
  const salesReportsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'sales-reports',
  );
  const vendorsDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'vendors');
  const vendorsIngestDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'vendors-catalog-ingest',
  );
  const debtDestination = ALL_DESTINATIONS.find((destination) => destination.id === 'debt');
  const competitorSignalsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'competitor-signals',
  );
  const competitorNewsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'competitor-news',
  );
  const competitorAlertsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'competitor-alerts',
  );
  const clientContactDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'client-contact-detail',
  );
  const legalMattersDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-matters',
  );
  const legalCounselDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-counsel',
  );
  const legalInvoicesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-invoices',
  );
  const legalCalendarDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-calendar',
  );
  const legalRiskDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-risk',
  );
  const legalReportsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'legal-reports',
  );
  const benefitsPlansDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-plans',
  );
  const benefitsEnrollmentDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-enrollment',
  );
  const benefitsVendorsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-vendors',
  );
  const benefitsCostsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-costs',
  );
  const benefitsClaimsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-claims',
  );
  const benefitsComplianceDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-compliance',
  );
  const benefitsDocumentsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-documents',
  );
  const benefitsReportsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'benefits-reports',
  );
  // M18: the last stage — the fee stack, the three bids, and the per-counterparty
  // register the sharpening reads from. `bid-counterparties` is deliberately NOT
  // M05's `bid-history`: that one is the portfolio-wide win/loss view, and two
  // destinations sharing an id would resolve to whichever came first.
  const bidAssemblyDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bid-assembly',
  );
  const bidCounterpartiesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'bid-counterparties',
  );
  const payrollExportsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'payroll-exports',
  );
  const payrollCostDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'payroll-cost',
  );
  // M08 field operations. The capture screen is a child of the daily-report
  // list and shares its destination, exactly as the estimate workspace does.
  const fieldTodayDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'field-today',
  );
  const fieldDailyReportsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'field-daily-reports',
  );
  const fieldDispatchDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'field-dispatch',
  );
  const fieldObservationsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'field-observations',
  );
  const fieldTasksDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'field-tasks',
  );
  const fieldRfisDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'field-rfis',
  );
  const complianceBoardDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'compliance-board',
  );
  // M10 inbox intelligence. The conversation view is a child of the triage
  // board and shares its destination, exactly as the estimate workspace does.
  const inboxTriageDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'inbox-triage',
  );
  const estimatingAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'estimating-agent-detail',
  );
  const qaQcAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'qa-qc-agent-detail',
  );
  const hrWorkforceAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'hr-workforce-agent-detail',
  );
  const equipmentAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'equipment-agent-detail',
  );
  const marketIntelligenceAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'market-intelligence-agent-detail',
  );
  const growthMaAgentDetailDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'growth-ma-agent-detail',
  );
  const marketIntelligenceCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'market-intelligence',
  );
  const growthMaCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'growth-ma',
  );
  const financeCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'finance',
  );
  const procurementCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'procurement',
  );
  const schedulingCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'scheduling',
  );
  const safetyCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'safety',
  );
  const qualityCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'quality',
  );
  const approvalsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'approvals',
  );
  const timeAutomationDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'time-automation',
  );
  const timeAutomationMonitoringDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'time-automation-monitoring',
  );
  const timeAutomationProvisioningDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'time-automation-provisioning',
  );
  const timeAutomationFieldTimeDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'time-automation-field-time',
  );
  const timeAutomationReconciliationDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'time-automation-reconciliation',
  );
  const timeAutomationFieldTimeSetupDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'time-automation-field-time-setup',
  );
  const resourcesCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'resources',
  );
  const documentsCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'documents',
  );
  const reportsCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'reports',
  );
  const integrationsCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'integrations',
  );
  const settingsPreferencesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-preferences',
  );
  const settingsNotificationsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-notifications',
  );
  const settingsFeatureFlagsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-feature-flags',
  );
  const settingsAiDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-ai',
  );
  const settingsAccessDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-access',
  );
  const settingsRetentionDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-retention',
  );
  const settingsPlanDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-plan',
  );
  const settingsUsersDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-users',
  );
  const settingsRolesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-roles',
  );
  const settingsTenantsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-tenants',
  );
  const settingsAuditDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-audit',
  );
  const settingsPurgeDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-purge',
  );
  const settingsActivityDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'settings-activity',
  );
  const simulationsCommandCenterDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'simulations',
  );
  const portalSelectionsDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'portal-selections',
  );
  // M12. The showcase builder is a child of the portfolio list and shares its
  // destination, exactly as the estimate workspace shares the estimates one.
  const opsDashboardDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'ops-dashboard',
  );
  const portfolioShowcasesDestination = ALL_DESTINATIONS.find(
    (destination) => destination.id === 'portfolio-showcases',
  );

  return (
    <Routes>
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/company-setup/:setupId?" element={<CompanySetupPage />} />
      <Route
        path="/payroll/oauth/callback"
        element={
          <LazyRoute>
            <NativePayrollOAuthCallbackPage />
          </LazyRoute>
        }
      />
      <Route
        path="/workspace/oauth/callback"
        element={
          <LazyRoute>
            <M365OAuthCallbackPage />
          </LazyRoute>
        }
      />
      <Route path="/auth/password-change" element={<PasswordChangePage />} />
      <Route
        path="/privacy/integrations"
        element={
          <LazyRoute>
            <IntegrationPrivacyPage />
          </LazyRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <LazyRoute>
            <NewHireOnboardingPage />
          </LazyRoute>
        }
      />
      {/* M11 client portal — its own tree, outside the staff AppShell. */}
      <Route
        path="/portal/login"
        element={
          <LazyRoute>
            <PortalLoginPage />
          </LazyRoute>
        }
      />
      {/* New-hire onboarding portal — token in header after the email link is opened. */}
      <Route
        path="/onboarding/hire"
        element={
          <LazyRoute>
            <NewHireOnboardingPage />
          </LazyRoute>
        }
      />
      {/* Client Launch invite portal — CLC only, no ConstructionOS modules. */}
      <Route
        path="/portal/launch/login"
        element={
          <LazyRoute>
            <ClientLaunchPortalLoginPage />
          </LazyRoute>
        }
      />
      <Route
        path="/portal/launch"
        element={
          <LazyRoute>
            <ClientLaunchPortalShell />
          </LazyRoute>
        }
      >
        <Route
          index
          element={
            <LazyRoute>
              <ClientLaunchOverviewPage />
            </LazyRoute>
          }
        />
        <Route
          path="guide"
          element={
            <LazyRoute>
              <ClientLaunchGuidePage />
            </LazyRoute>
          }
        />
        <Route
          path="attention"
          element={
            <LazyRoute>
              <ClientLaunchAttentionPage />
            </LazyRoute>
          }
        />
        <Route
          path="systems"
          element={
            <LazyRoute>
              <ClientLaunchSystemsPage />
            </LazyRoute>
          }
        />
        <Route
          path="vendors"
          element={
            <LazyRoute>
              <ClientLaunchVendorsPage />
            </LazyRoute>
          }
        />
        <Route
          path="vendors/:id"
          element={
            <LazyRoute>
              <ClientLaunchVendorDetailPage />
            </LazyRoute>
          }
        />
        <Route
          path="spend"
          element={
            <LazyRoute>
              <ClientLaunchSpendPage />
            </LazyRoute>
          }
        />
        <Route
          path="documents"
          element={
            <LazyRoute>
              <ClientLaunchDocumentsPage />
            </LazyRoute>
          }
        />
        <Route
          path="integrations"
          element={
            <LazyRoute>
              <ClientLaunchIntegrationsPage />
            </LazyRoute>
          }
        />
        <Route
          path="tasks"
          element={
            <LazyRoute>
              <ClientLaunchTasksPage />
            </LazyRoute>
          }
        />
        <Route
          path="support"
          element={
            <LazyRoute>
              <ClientLaunchSupportPage />
            </LazyRoute>
          }
        />
        <Route path="onboarding" element={<Navigate to="/portal/launch/guide" replace />} />
        <Route path="decisions" element={<Navigate to="/portal/launch/support" replace />} />
      </Route>
      <Route
        path="/portal"
        element={
          <LazyRoute>
            <PortalShell />
          </LazyRoute>
        }
      >
        <Route index element={<PortalHomePage />} />
        <Route path="projects" element={<PortalProjectsPage />} />
        <Route path="documents" element={<PortalSharedDocumentsPage />} />
        <Route path="messages" element={<PortalConversationsPage />} />
        <Route
          path="ask"
          element={
            <PortalParticipantGuard allowed={['SUBCONTRACTOR', 'VENDOR']}>
              <PortalAiPage />
            </PortalParticipantGuard>
          }
        />
        <Route
          path="guide"
          element={
            <PortalParticipantGuard allowed={['SUBCONTRACTOR', 'VENDOR']}>
              <PortalGuidePage />
            </PortalParticipantGuard>
          }
        />
        <Route
          path="catalog"
          element={
            <PortalParticipantGuard allowed={['VENDOR']}>
              <PortalCatalogPage />
            </PortalParticipantGuard>
          }
        />
        <Route
          path="work"
          element={
            <PortalParticipantGuard allowed={['SUBCONTRACTOR', 'VENDOR']}>
              <PortalWorkPage />
            </PortalParticipantGuard>
          }
        />
        <Route
          path="rfq/:quoteRequestId"
          element={
            <PortalParticipantGuard allowed={['SUBCONTRACTOR', 'VENDOR']}>
              <PortalRfqPage />
            </PortalParticipantGuard>
          }
        />
        <Route path="money" element={<PortalMoneyPage />} />
        <Route
          path="onboarding"
          element={
            <PortalParticipantGuard allowed={['SUBCONTRACTOR', 'VENDOR']}>
              <PortalOnboardingPage />
            </PortalParticipantGuard>
          }
        />
        <Route
          path="compliance"
          element={
            <PortalParticipantGuard allowed={['SUBCONTRACTOR', 'VENDOR']}>
              <PortalCompliancePage />
            </PortalParticipantGuard>
          }
        />
        <Route path="projects/:id/status" element={<PortalStatusPage />} />
        <Route
          path="projects/:id/selections"
          element={
            <PortalParticipantGuard allowed={['CLIENT']}>
              <PortalSelectionsPage />
            </PortalParticipantGuard>
          }
        />
        <Route path="projects/:id/photos" element={<PortalPhotosPage />} />
        <Route path="projects/:id/documents" element={<PortalDocumentsPage />} />
        <Route path="projects/:id/messages" element={<PortalMessagesPage />} />
        <Route
          path="projects/:id/decisions"
          element={
            <PortalParticipantGuard allowed={['CLIENT']}>
              <PortalDecisionLogPage />
            </PortalParticipantGuard>
          }
        />
        <Route path="projects/:id" element={<Navigate to="status" replace />} />
      </Route>
      <Route element={<ProtectedShell />}>
        <Route
          path="settings/portal"
          element={
            <ProtectedAnyPermission permissions={PORTAL_ADMIN_ANY_PERMISSIONS}>
              <LazyRoute>
                <PortalAdministrationPage />
              </LazyRoute>
            </ProtectedAnyPermission>
          }
        />
        <Route
          index
          element={
            executiveSummaryDestination ? (
              <ProtectedLiveDestination destination={executiveSummaryDestination}>
                <ExecutiveSummaryPage />
              </ProtectedLiveDestination>
            ) : (
              <ExecutiveSummaryPage />
            )
          }
        />
        <Route path="executive-summary" element={<LegacyExecutiveSummaryRedirect />} />
        {m365WorkspaceDestination ? (
          <>
            <Route
              path="workspace"
              element={
                <ProtectedLiveDestination destination={m365WorkspaceDestination}>
                  <LazyRoute>
                    <M365WorkspacePage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path="workspace/oauth/callback"
              element={
                <ProtectedLiveDestination destination={m365WorkspaceDestination}>
                  <LazyRoute>
                    <M365OAuthCallbackPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </>
        ) : null}
        {googleWorkspaceDestination ? (
          <Route
            path="google-workspace"
            element={
              <ProtectedLiveDestination destination={googleWorkspaceDestination}>
                <LazyRoute>
                  <GoogleWorkspacePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {clientLaunchDestination ? (
          <Route
            path="client-launch"
            element={
              <ProtectedLiveDestination destination={clientLaunchDestination}>
                <LazyRoute>
                  <ClientLaunchShell />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          >
            <Route index element={<ClientLaunchStaffIndex />} />
            <Route
              path="overview"
              element={
                <LazyRoute>
                  <ClientLaunchOverviewPage />
                </LazyRoute>
              }
            />
            <Route
              path="readiness"
              element={
                <ProtectedPermission permission={PERMISSIONS.CLIENT_LAUNCH_MANAGE}>
                  <LazyRoute>
                    <ClientLaunchReadinessPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="guide"
              element={
                <LazyRoute>
                  <ClientLaunchGuidePage />
                </LazyRoute>
              }
            />
            <Route
              path="attention"
              element={
                <LazyRoute>
                  <ClientLaunchAttentionPage />
                </LazyRoute>
              }
            />
            <Route
              path="onboarding"
              element={
                <LazyRoute>
                  <ClientLaunchOnboardingPage />
                </LazyRoute>
              }
            />
            <Route
              path="systems"
              element={
                <LazyRoute>
                  <ClientLaunchSystemsPage />
                </LazyRoute>
              }
            />
            <Route
              path="vendors"
              element={
                <LazyRoute>
                  <ClientLaunchVendorsPage />
                </LazyRoute>
              }
            />
            <Route
              path="vendors/:id"
              element={
                <LazyRoute>
                  <ClientLaunchVendorDetailPage />
                </LazyRoute>
              }
            />
            <Route
              path="spend"
              element={
                <LazyRoute>
                  <ClientLaunchSpendPage />
                </LazyRoute>
              }
            />
            <Route
              path="documents"
              element={
                <LazyRoute>
                  <ClientLaunchDocumentsPage />
                </LazyRoute>
              }
            />
            <Route
              path="integrations"
              element={
                <LazyRoute>
                  <ClientLaunchIntegrationsPage />
                </LazyRoute>
              }
            />
            <Route
              path="decisions"
              element={
                <LazyRoute>
                  <ClientLaunchDecisionsPage />
                </LazyRoute>
              }
            />
            <Route
              path="tasks"
              element={
                <LazyRoute>
                  <ClientLaunchTasksPage />
                </LazyRoute>
              }
            />
            <Route
              path="support"
              element={
                <LazyRoute>
                  <ClientLaunchSupportPage />
                </LazyRoute>
              }
            />
            <Route
              path="attention"
              element={
                <LazyRoute>
                  <ClientLaunchDecisionsPage />
                </LazyRoute>
              }
            />
            <Route
              path="control-room"
              element={
                <LazyRoute>
                  <ClientLaunchControlRoomPage />
                </LazyRoute>
              }
            />
          </Route>
        ) : null}
        <Route path="meyer" element={<Navigate to="/portal/launch" replace />} />
        {/*
          Settings chrome is permission-filtered, not gated on SETTINGS_MANAGE.
          Sibling sections (integrations, testing, …) must stay reachable
          when organization settings is denied. Each child keeps its own
          ProtectedLiveDestination.
        */}
        <Route path="settings" element={<SettingsShell />}>
          <Route index element={<SettingsIndex />} />
          {settingsPreferencesDestination ? (
            <Route
              path="preferences"
              element={
                <ProtectedLiveDestination destination={settingsPreferencesDestination}>
                  <PreferencesSettingsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsNotificationsDestination ? (
            <Route
              path="notifications"
              element={
                <ProtectedLiveDestination destination={settingsNotificationsDestination}>
                  <NotificationsSettingsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsFeatureFlagsDestination ? (
            <Route
              path="feature-flags"
              element={
                <ProtectedLiveDestination destination={settingsFeatureFlagsDestination}>
                  <FeatureFlagsSettingsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsAiDestination ? (
            <Route
              path="ai"
              element={
                <ProtectedLiveDestination destination={settingsAiDestination}>
                  <AiSettingsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsAccessDestination ? (
            <Route
              path="access"
              element={
                <ProtectedLiveDestination destination={settingsAccessDestination}>
                  <AccessSettingsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsRetentionDestination ? (
            <Route
              path="retention"
              element={
                <ProtectedLiveDestination destination={settingsRetentionDestination}>
                  <RetentionSettingsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsPlanDestination ? (
            <Route
              path="plan"
              element={
                <ProtectedLiveDestination destination={settingsPlanDestination}>
                  <PlanSettingsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsUsersDestination ? (
            <Route
              path="users"
              element={
                <ProtectedLiveDestination destination={settingsUsersDestination}>
                  <SettingsUsersPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsRolesDestination ? (
            <Route
              path="roles"
              element={
                <ProtectedLiveDestination destination={settingsRolesDestination}>
                  <SettingsRolesPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsTenantsDestination ? (
            <Route
              path="tenants"
              element={
                <ProtectedLiveDestination destination={settingsTenantsDestination}>
                  <SettingsTenantsPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsAuditDestination ? (
            <Route
              path="audit"
              element={
                <ProtectedLiveDestination destination={settingsAuditDestination}>
                  <SettingsAuditPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsPurgeDestination ? (
            <Route
              path="danger-zone"
              element={
                <ProtectedLiveDestination destination={settingsPurgeDestination}>
                  <PlatformPurgePage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {settingsActivityDestination ? (
            <Route
              path="activity"
              element={
                <ProtectedLiveDestination destination={settingsActivityDestination}>
                  <SettingsActivityPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {integrationsCommandCenterDestination ? (
            <Route
              path="integrations"
              element={
                <ProtectedLiveDestination destination={integrationsCommandCenterDestination}>
                  <LazyRoute>
                    <IntegrationsCommandCenterPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {buildStatusDestination ? (
            <Route
              path="build-status"
              element={
                <ProtectedLiveDestination destination={buildStatusDestination}>
                  <BuildStatusPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {seedDataDestination && visibleInThisBuild(['seed-data']).length ? (
            <Route
              path="seed-data"
              element={
                <ProtectedLiveDestination destination={seedDataDestination}>
                  <SeedDataCommandCenterPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {testingDestination ? (
            <Route
              path="testing"
              element={
                <ProtectedLiveDestination destination={testingDestination}>
                  <TestingCommandCenterPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {securityDestination ? (
            <Route
              path="security"
              element={
                <ProtectedLiveDestination destination={securityDestination}>
                  <SecurityCommandCenterPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {infraDestination ? (
            <Route
              path="infra"
              element={
                <ProtectedLiveDestination destination={infraDestination}>
                  <InfraCommandCenterPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {observabilityDestination ? (
            <Route
              path="observability"
              element={
                <ProtectedLiveDestination destination={observabilityDestination}>
                  <ObservabilityCommandCenterPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          {launchDestination ? (
            <Route
              path="launch"
              element={
                <ProtectedLiveDestination destination={launchDestination}>
                  <LaunchCommandCenterPage />
                </ProtectedLiveDestination>
              }
            />
          ) : null}
          <Route path="*" element={<SettingsUnknownSection />} />
        </Route>
        {Object.entries(LEGACY_SETTINGS_REDIRECTS).map(([from, to]) => (
          <Route key={from} path={from.replace(/^\//, '')} element={<Navigate to={to} replace />} />
        ))}
        {projectMapDestination ? (
          <Route
            path="projects/map"
            element={
              <ProtectedLiveDestination destination={projectMapDestination}>
                <ProjectMapPage />
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {projectPortfolioDestination ? (
          <Route
            path="projects"
            element={
              <ProtectedLiveDestination destination={projectPortfolioDestination}>
                <LazyRoute>
                  <ProjectPortfolioPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {projectOverviewDestination ? (
          <Route
            path="projects/overview"
            element={
              <ProtectedLiveDestination destination={projectOverviewDestination}>
                <LazyRoute>
                  <ProjectOverviewPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {projectBudgetDestination ? (
          <Route
            path="projects/budget"
            element={
              <ProtectedLiveDestination destination={projectBudgetDestination}>
                <LazyRoute>
                  <ProjectBudgetPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {projectScheduleDestination ? (
          <Route
            path="projects/schedule"
            element={
              <ProtectedLiveDestination destination={projectScheduleDestination}>
                <LazyRoute>
                  <ProjectSchedulePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {rfiDestination ? (
          <Route
            path="projects/rfis"
            element={
              <ProtectedLiveDestination destination={rfiDestination}>
                <LazyRoute>
                  <ProjectControlsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {submittalDestination ? (
          <Route
            path="projects/submittals"
            element={
              <ProtectedLiveDestination destination={submittalDestination}>
                <LazyRoute>
                  <ProjectControlsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {changeOrderDestination ? (
          <Route
            path="projects/change-orders"
            element={
              <ProtectedLiveDestination destination={changeOrderDestination}>
                <LazyRoute>
                  <ProjectControlsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {portalSelectionsDestination ? (
          <Route
            path="projects/selections"
            element={
              <ProtectedLiveDestination destination={portalSelectionsDestination}>
                <LazyRoute>
                  <SelectionsAuthoringPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {unifiedCrmDestination ? (
          <>
            <Route
              path="crm/identities/:id"
              element={
                <ProtectedLiveDestination destination={unifiedCrmDestination}>
                  <LazyRoute>
                    <UnifiedCrmPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path="crm/:view?"
              element={
                <ProtectedLiveDestination destination={unifiedCrmDestination}>
                  <LazyRoute>
                    <UnifiedCrmPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </>
        ) : null}
        {clientAccountsDestination ? (
          <Route
            path="crm/clients"
            element={
              <ProtectedLiveDestination destination={clientAccountsDestination}>
                <LazyRoute>
                  <ClientAccountsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {clientContactDetailDestination ? (
          <Route
            path="crm/clients/contacts/:id"
            element={
              <ProtectedLiveDestination destination={clientContactDetailDestination}>
                <LazyRoute>
                  <ClientContactDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {clientAccountsDestination ? (
          <Route
            path="crm/clients/:id"
            element={
              <ProtectedLiveDestination destination={clientAccountsDestination}>
                <LazyRoute>
                  <ClientAccountDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {/* Client Accounts moved into Relationships; keep bookmarks, notifications and source links working. */}
        <Route
          path="clients/*"
          element={<RelocatedPrefixRedirect from="/clients" to="/crm/clients" />}
        />
        <Route path="estimating/leads" element={<Navigate to="/estimating/bid-intake" replace />} />
        {leadFunnelDestination ? (
          <Route
            path="estimating/leads/:id"
            element={
              <ProtectedLiveDestination destination={leadFunnelDestination}>
                <LazyRoute>
                  <LeadDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {/*
          P4 bid intake. Gated on its own permission key rather than on a
          navigation destination: the nav registry is owned elsewhere, and a
          route conditioned on a missing registry entry renders nothing at all,
          which is a dead route rather than a missing menu item.
        */}
        <Route path="estimating/workspace" element={<Navigate to="/estimating/command" replace />} />
        <Route path="estimating/command" element={<ProtectedPermission permission={PERMISSIONS.ESTIMATING_ESTIMATES_READ}><LazyRoute><CommandCenterPage /></LazyRoute></ProtectedPermission>} />
        <Route path="estimating/projects" element={<ProtectedPermission permission={PERMISSIONS.ESTIMATING_ESTIMATES_READ}><LazyRoute><ProjectsPage /></LazyRoute></ProtectedPermission>} />
        <Route path="estimating/projects/:estimateId" element={<ProtectedPermission permission={PERMISSIONS.ESTIMATING_ESTIMATES_READ}><LazyRoute><ProjectWorkspacePage /></LazyRoute></ProtectedPermission>} />
        <Route path="estimating/projects/:estimateId/:tab" element={<ProtectedPermission permission={PERMISSIONS.ESTIMATING_ESTIMATES_READ}><LazyRoute><ProjectWorkspacePage /></LazyRoute></ProtectedPermission>} />
        <Route path="estimating/register" element={<ProtectedPermission permission={PERMISSIONS.ESTIMATING_ESTIMATES_READ}><LazyRoute><BidRegisterArea /></LazyRoute></ProtectedPermission>} />
        <Route path="estimating/intelligence" element={<ProtectedPermission permission={PERMISSIONS.ESTIMATING_ESTIMATES_READ}><LazyRoute><IntelligenceArea /></LazyRoute></ProtectedPermission>} />
        {bidIntakeDestination ? (
          <Route
            path="estimating/bid-intake"
            element={
              <ProtectedLiveDestination destination={bidIntakeDestination}>
                <LazyRoute>
                  <IntakePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {estimatingConfigDestination ? (
          <Route
            path="estimating/config"
            element={
              <ProtectedLiveDestination destination={estimatingConfigDestination}>
                <LazyRoute>
                  <SettingsArea />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {/*
          P6 estimating pipeline. Gated on its own permission key rather than on
          a navigation destination: `navigation/registry.ts` is owned elsewhere,
          and a route conditioned on a missing registry entry renders nothing at
          all — a dead route rather than a missing menu item. The page is
          reachable by URL and from every stage screen either way, and it
          upgrades to a sidebar entry the moment the registry gains one.
        */}
        <Route
          path="estimating/pipeline"
          element={
            <ProtectedPermission permission={PERMISSIONS.ESTIMATING_PIPELINE_READ}>
              <LazyRoute>
                <EstimatingPipelinePage />
              </LazyRoute>
            </ProtectedPermission>
          }
        />
        {estimatingEquipmentDestination ? (
          <Route
            path="estimating/equipment"
            element={
              <ProtectedLiveDestination destination={estimatingEquipmentDestination}>
                <LazyRoute>
                  <EquipmentPlanPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {costDatabaseDestination ? (
          <>
            <Route
              path="estimating/cost-database"
              element={
                <ProtectedLiveDestination destination={costDatabaseDestination}>
                  <LazyRoute>
                    <CostBookEntry />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {/* The original price-revision and publication tool stays reachable. */}
            <Route
              path="estimating/cost-database/manage"
              element={
                <ProtectedLiveDestination destination={costDatabaseDestination}>
                  <LazyRoute>
                    <CostDatabasePage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </>
        ) : null}
        {estimatesDestination ? (
          <Route
            path="estimating/estimates"
            element={
              <ProtectedLiveDestination destination={estimatesDestination}>
                <LazyRoute>
                  <EstimatesListPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {estimatesDestination ? (
          <Route
            path="estimating/estimates/:id"
            element={
              <ProtectedLiveDestination destination={estimatesDestination}>
                <LazyRoute>
                  <EstimateWorkspacePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {bidHistoryDestination ? (
          <Route
            path="estimating/history"
            element={
              <ProtectedLiveDestination destination={bidHistoryDestination}>
                <LazyRoute>
                  <BidHistoryPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {billingApplicationsDestination ? (
          <Route
            path="finance/billing"
            element={
              <ProtectedLiveDestination destination={billingApplicationsDestination}>
                <LazyRoute>
                  <BillingApplicationsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {billingApplicationsDestination ? (
          <Route
            path="finance/billing/:id"
            element={
              <ProtectedLiveDestination destination={billingApplicationsDestination}>
                <LazyRoute>
                  <BillingApplicationDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {billingCollectionsDestination ? (
          <Route
            path="finance/collections"
            element={
              <ProtectedLiveDestination destination={billingCollectionsDestination}>
                <LazyRoute>
                  <CollectionsAgingPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {jobCostExportDestination ? (
          <Route
            path="finance/job-cost-export"
            element={
              <ProtectedLiveDestination destination={jobCostExportDestination}>
                <LazyRoute>
                  <JobCostExportPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {(['cash', 'receivables', 'payables', 'projects', 'books', 'controls'] as const).map(
          (area) => (
            <Route
              key={`unified-finance-${area}`}
              path={`finance/${area}`}
              element={
                <LazyRoute>
                  <FinanceAreaEntry area={area} />
                </LazyRoute>
              }
            />
          ),
        )}
        {financeAnalyticsDestination ? (
          <Route
            path="finance/analytics"
            element={
              <ProtectedLiveDestination destination={financeAnalyticsDestination}>
                <LazyRoute>
                  <FinanceAnalyticsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {financeSubjectsDestination ? (
          <Route
            path="finance/subjects"
            element={
              <ProtectedLiveDestination destination={financeSubjectsDestination}>
                <LazyRoute>
                  <FinanceImportedSubjectsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {quickBooksReportsDestination ? (
          <Route
            path="accounting/reports"
            element={
              <ProtectedLiveDestination destination={quickBooksReportsDestination}>
                <LazyRoute>
                  <QuickBooksReportsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {accountantWorkspaceDestination ? (
          <Route
            path="accounting/workspace"
            element={
              <ProtectedLiveDestination destination={accountantWorkspaceDestination}>
                <LazyRoute>
                  <AccountantWorkspacePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {financeTaxesDestination ? (
          <Route
            path="finance/taxes"
            element={
              <ProtectedLiveDestination destination={financeTaxesDestination}>
                <LazyRoute>
                  <FinanceTaxesPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {incomingBillsDestination ? (
          <Route
            path="finance/payables/incoming"
            element={
              <ProtectedLiveDestination destination={incomingBillsDestination}>
                <LazyRoute>
                  <IncomingBillsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        <Route path="reports/library" element={<Navigate to="/reports?view=library" replace />} />
        <Route
          path="reports/library/:reportKey"
          element={
            <LazyRoute>
              <ReportPage />
            </LazyRoute>
          }
        />
        {bankAccountsDestination ? (
          <Route
            path="finance/accounts"
            element={
              <ProtectedLiveDestination destination={bankAccountsDestination}>
                <LazyRoute>
                  <UnifiedBankAccountsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {/* Legacy top-level QuickBooks URLs — now tabs on the Accounting workspace. */}
        <Route path="accounting/mappings" element={<Navigate to="/banking/mappings" replace />} />
        <Route path="accounting/sync" element={<Navigate to="/banking/sync" replace />} />
        {bankAccountsDestination ? (
          <Route
            path="banking"
            element={
              <ProtectedHubLayout destination={bankAccountsDestination}>
                <LazyRoute>
                  <BankingWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={bankAccountsDestination}>
                  <LazyRoute>
                    <BankAccountsPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path="plaid-return"
              element={
                <LazyRoute>
                  <PlaidOAuthReturnPage />
                </LazyRoute>
              }
            />
            {bankTransactionsDestination ? (
              <Route
                path="transactions"
                element={
                  <ProtectedLiveDestination destination={bankTransactionsDestination}>
                    <LazyRoute>
                      <BankTransactionsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {bankSpendingDestination ? (
              <Route
                path="spending"
                element={
                  <ProtectedLiveDestination destination={bankSpendingDestination}>
                    <LazyRoute>
                      <BankSpendingPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {bankStatementsDestination ? (
              <Route
                path="statements"
                element={
                  <ProtectedLiveDestination destination={bankStatementsDestination}>
                    <LazyRoute>
                      <BankStatementsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {bankReconciliationDestination ? (
              <Route
                path="reconciliation"
                element={
                  <ProtectedLiveDestination destination={bankReconciliationDestination}>
                    <LazyRoute>
                      <BankReconciliationPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {accountingMappingsDestination ? (
              <Route
                path="mappings"
                element={
                  <ProtectedLiveDestination destination={accountingMappingsDestination}>
                    <LazyRoute>
                      <AccountingMappingsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {accountingSyncDestination ? (
              <Route
                path="sync"
                element={
                  <ProtectedLiveDestination destination={accountingSyncDestination}>
                    <LazyRoute>
                      <AccountingSyncPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
          </Route>
        ) : null}
        {takeoffDestination ? (
          <Route
            path="takeoff"
            element={
              <ProtectedLiveDestination destination={takeoffDestination}>
                <LazyRoute>
                  <TakeoffPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {takeoffDestination ? (
          <Route
            path="takeoff/runs/:runId"
            element={
              <ProtectedLiveDestination destination={takeoffDestination}>
                <LazyRoute>
                  <TakeoffReviewPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {compingDestination ? (
          <Route
            path="comping"
            element={
              <ProtectedLiveDestination destination={compingDestination}>
                <LazyRoute>
                  <CompingPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {laborDestination ? (
          <Route
            path="labor-plan"
            element={
              <ProtectedLiveDestination destination={laborDestination}>
                <LazyRoute>
                  <LaborPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {hrDestination ? (
          <Route
            path="hr"
            element={
              <ProtectedHubLayout destination={hrDestination}>
                <LazyRoute>
                  <HrWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={hrDestination}>
                  <LazyRoute>
                    <HrOverviewPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route path="roster" element={<Navigate to="/hr/employees" replace />} />
            <Route path="employees">
              <Route
                index
                element={
                  <ProtectedPermission permission={PERMISSIONS.HR_EMPLOYEES_READ}>
                    <LazyRoute>
                      <HrEmployeesPage />
                    </LazyRoute>
                  </ProtectedPermission>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedPermission permission={PERMISSIONS.HR_EMPLOYEES_READ}>
                    <LazyRoute>
                      <HrEmployeeProfilePage />
                    </LazyRoute>
                  </ProtectedPermission>
                }
              />
            </Route>
            <Route
              path="compensation"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_COMPENSATION_READ}>
                  <LazyRoute>
                    <HrCompensationPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="time"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_TIME_ENTRIES_READ}>
                  <LazyRoute>
                    <HrTimePage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="performance"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_REVIEWS_READ}>
                  <LazyRoute>
                    <HrPerformancePage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="documents"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_DOCUMENTS_READ}>
                  <LazyRoute>
                    <HrDocumentsPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="onboarding"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_EMPLOYEES_READ}>
                  <LazyRoute>
                    <HrOnboardingPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="analytics"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_EMPLOYEES_READ}>
                  <LazyRoute>
                    <HrAnalyticsPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="org-chart"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_EMPLOYEES_READ}>
                  <LazyRoute>
                    <HrOrgChartPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="compliance"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_EMPLOYEES_READ}>
                  <LazyRoute>
                    <HrCompliancePage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="imports"
              element={
                <ProtectedPermission permission={PERMISSIONS.HR_IMPORTS_READ}>
                  <LazyRoute>
                    <HrImportsPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route path="benefits" element={<Navigate to="/benefits" replace />} />
          </Route>
        ) : null}
        {benefitsDestination ? (
          <Route
            path="benefits"
            element={
              <ProtectedHubLayout destination={benefitsDestination}>
                <LazyRoute>
                  <BenefitsWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={benefitsDestination}>
                  <LazyRoute>
                    <BenefitsOverviewPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {benefitsPlansDestination ? (
              <Route path="plans">
                <Route
                  index
                  element={
                    <ProtectedLiveDestination destination={benefitsPlansDestination}>
                      <LazyRoute>
                        <BenefitsPlansPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedLiveDestination destination={benefitsPlansDestination}>
                      <LazyRoute>
                        <BenefitsPlanDetailPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
              </Route>
            ) : null}
            {benefitsEnrollmentDestination ? (
              <Route
                path="enrollment"
                element={
                  <ProtectedLiveDestination destination={benefitsEnrollmentDestination}>
                    <LazyRoute>
                      <BenefitsEnrollmentPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {benefitsVendorsDestination ? (
              <Route
                path="vendors"
                element={
                  <ProtectedLiveDestination destination={benefitsVendorsDestination}>
                    <LazyRoute>
                      <BenefitsVendorsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {benefitsCostsDestination ? (
              <Route
                path="costs"
                element={
                  <ProtectedLiveDestination destination={benefitsCostsDestination}>
                    <LazyRoute>
                      <BenefitsCostsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {benefitsClaimsDestination ? (
              <Route
                path="claims"
                element={
                  <ProtectedLiveDestination destination={benefitsClaimsDestination}>
                    <LazyRoute>
                      <BenefitsClaimsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {benefitsComplianceDestination ? (
              <Route
                path="compliance"
                element={
                  <ProtectedLiveDestination destination={benefitsComplianceDestination}>
                    <LazyRoute>
                      <BenefitsCompliancePage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {benefitsDocumentsDestination ? (
              <Route
                path="documents"
                element={
                  <ProtectedLiveDestination destination={benefitsDocumentsDestination}>
                    <LazyRoute>
                      <BenefitsDocumentsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {benefitsReportsDestination ? (
              <Route
                path="reports"
                element={
                  <ProtectedLiveDestination destination={benefitsReportsDestination}>
                    <LazyRoute>
                      <BenefitsReportsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
          </Route>
        ) : null}
        {generalLedgerDestination ? (
          <Route
            path="general-ledger"
            element={
              <ProtectedLiveDestination destination={generalLedgerDestination}>
                <LazyRoute>
                  <GeneralLedgerPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {insuranceDestination ? (
          <Route
            path="insurance"
            element={
              <ProtectedHubLayout destination={insuranceDestination}>
                <LazyRoute>
                  <InsuranceWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={insuranceDestination}>
                  <LazyRoute>
                    <InsuranceOverviewPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {insuranceAlertsDestination ? (
              <Route
                path="alerts"
                element={
                  <ProtectedLiveDestination destination={insuranceAlertsDestination}>
                    <LazyRoute>
                      <InsuranceAlertsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insuranceProfileDestination ? (
              <Route
                path="profile"
                element={
                  <ProtectedLiveDestination destination={insuranceProfileDestination}>
                    <LazyRoute>
                      <InsuranceProfilePage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insuranceMarketDestination ? (
              <Route
                path="market"
                element={
                  <ProtectedLiveDestination destination={insuranceMarketDestination}>
                    <LazyRoute>
                      <InsuranceMarketPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insuranceIntakeDestination ? (
              <Route
                path="intake"
                element={
                  <ProtectedLiveDestination destination={insuranceIntakeDestination}>
                    <LazyRoute>
                      <PolicyIntakePage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insurancePoliciesDestination ? (
              <Route path="policies">
                <Route
                  index
                  element={
                    <ProtectedLiveDestination destination={insurancePoliciesDestination}>
                      <LazyRoute>
                        <InsurancePoliciesPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedLiveDestination destination={insurancePoliciesDestination}>
                      <LazyRoute>
                        <InsurancePolicyDetailPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
                <Route
                  path=":id/analysis"
                  element={
                    <ProtectedLiveDestination destination={insurancePoliciesDestination}>
                      <LazyRoute>
                        <PolicyAnalysisPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
              </Route>
            ) : null}
            {insuranceClaimsDestination ? (
              <Route
                path="claims"
                element={
                  <ProtectedLiveDestination destination={insuranceClaimsDestination}>
                    <LazyRoute>
                      <InsuranceClaimsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insurancePremiumsDestination ? (
              <Route
                path="premiums"
                element={
                  <ProtectedLiveDestination destination={insurancePremiumsDestination}>
                    <LazyRoute>
                      <InsurancePremiumsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insuranceCoverageDestination ? (
              <Route
                path="coverage"
                element={
                  <ProtectedLiveDestination destination={insuranceCoverageDestination}>
                    <LazyRoute>
                      <InsuranceCoveragePage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insuranceCarriersDestination ? (
              <Route
                path="carriers"
                element={
                  <ProtectedLiveDestination destination={insuranceCarriersDestination}>
                    <LazyRoute>
                      <InsuranceCarriersPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insuranceDocumentsDestination ? (
              <Route
                path="documents"
                element={
                  <ProtectedLiveDestination destination={insuranceDocumentsDestination}>
                    <LazyRoute>
                      <InsuranceDocumentsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {insuranceCoverageGapsDestination ? (
              <Route
                path="coverage-gaps"
                element={
                  <ProtectedLiveDestination destination={insuranceCoverageGapsDestination}>
                    <LazyRoute>
                      <PolicyAnalysisGapsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
          </Route>
        ) : null}
        {marketingDestination ? (
          <Route
            path="marketing"
            element={
              <ProtectedHubLayout destination={marketingDestination}>
                <LazyRoute>
                  <MarketingWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={marketingDestination}>
                  <LazyRoute>
                    <MarketingOverviewPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {marketingContentDestination ? (
              <Route path="content">
                <Route
                  index
                  element={
                    <ProtectedLiveDestination destination={marketingContentDestination}>
                      <LazyRoute>
                        <MarketingContentPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedLiveDestination destination={marketingContentDestination}>
                      <LazyRoute>
                        <MarketingContentDetailPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
              </Route>
            ) : null}
            {marketingAutopilotDestination ? (
              <Route
                path="autopilot"
                element={
                  <ProtectedLiveDestination destination={marketingAutopilotDestination}>
                    <LazyRoute>
                      <MarketingAutopilotPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {marketingChannelsDestination ? (
              <Route
                path="channels"
                element={
                  <ProtectedLiveDestination destination={marketingChannelsDestination}>
                    <LazyRoute>
                      <MarketingChannelsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {marketingEngagementDestination ? (
              <Route
                path="engagement"
                element={
                  <ProtectedLiveDestination destination={marketingEngagementDestination}>
                    <LazyRoute>
                      <MarketingEngagementPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {marketingBrandStudioDestination ? (
              <Route
                path="brand-studio"
                element={
                  <ProtectedLiveDestination destination={marketingBrandStudioDestination}>
                    <LazyRoute>
                      <MarketingBrandStudioPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
          </Route>
        ) : null}
        {itEstateDestination ? (
          <Route
            path="technology"
            element={
              <ProtectedHubLayout destination={itEstateDestination}>
                <LazyRoute>
                  <TechnologyWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={itEstateDestination}>
                  <LazyRoute>
                    <TechnologyOverviewPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {itHardwareDestination ? (
              <Route
                path="hardware"
                element={
                  <ProtectedLiveDestination destination={itHardwareDestination}>
                    <LazyRoute>
                      <TechnologyHardwarePage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itSoftwareDestination ? (
              <Route
                path="software"
                element={
                  <ProtectedLiveDestination destination={itSoftwareDestination}>
                    <LazyRoute>
                      <TechnologySoftwarePage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itCamerasDestination ? (
              <Route
                path="cameras"
                element={
                  <ProtectedLiveDestination destination={itCamerasDestination}>
                    <LazyRoute>
                      <TechnologyCamerasPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itDevicesDestination ? (
              <Route
                path="devices"
                element={
                  <ProtectedLiveDestination destination={itDevicesDestination}>
                    <LazyRoute>
                      <TechnologyDevicesPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itLicensesDestination ? (
              <Route
                path="licenses"
                element={
                  <ProtectedLiveDestination destination={itLicensesDestination}>
                    <LazyRoute>
                      <TechnologyLicensesPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itDirectoryDestination ? (
              <Route
                path="directory"
                element={
                  <ProtectedLiveDestination destination={itDirectoryDestination}>
                    <LazyRoute>
                      <TechnologyDirectoryPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itNetworkDestination ? (
              <Route
                path="network"
                element={
                  <ProtectedLiveDestination destination={itNetworkDestination}>
                    <LazyRoute>
                      <TechnologyNetworkPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itTenantConfigDestination ? (
              <Route
                path="tenant"
                element={
                  <ProtectedLiveDestination destination={itTenantConfigDestination}>
                    <LazyRoute>
                      <TechnologyTenantConfigPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {itImportDestination ? (
              <Route
                path="import"
                element={
                  <ProtectedLiveDestination destination={itImportDestination}>
                    <LazyRoute>
                      <TechnologyImportPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
          </Route>
        ) : null}
        {salesPipelineDestination ? (
          <Route
            path="sales"
            element={
              <ProtectedHubLayout destination={salesPipelineDestination}>
                <LazyRoute>
                  <SalesWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={salesPipelineDestination}>
                  <LazyRoute>
                    <SalesPipelinePage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {salesProspectsDestination ? (
              <Route path="prospects">
                <Route
                  index
                  element={
                    <ProtectedLiveDestination destination={salesProspectsDestination}>
                      <LazyRoute>
                        <SalesProspectsPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedLiveDestination destination={salesProspectsDestination}>
                      <LazyRoute>
                        <SalesProspectDetailPage />
                      </LazyRoute>
                    </ProtectedLiveDestination>
                  }
                />
              </Route>
            ) : null}
            {salesActivityDestination ? (
              <Route
                path="activity"
                element={
                  <ProtectedLiveDestination destination={salesActivityDestination}>
                    <LazyRoute>
                      <SalesActivityPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {salesReportsDestination ? (
              <Route
                path="reports"
                element={
                  <ProtectedLiveDestination destination={salesReportsDestination}>
                    <LazyRoute>
                      <SalesReportsPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
          </Route>
        ) : null}
        {vendorsDestination ? (
          <Route path="crm/vendors">
            <Route
              index
              element={
                <ProtectedLiveDestination destination={vendorsDestination}>
                  <LazyRoute>
                    <VendorsDirectoryPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {vendorsIngestDestination ? (
              <Route
                path="ingest"
                element={
                  <ProtectedLiveDestination destination={vendorsIngestDestination}>
                    <LazyRoute>
                      <VendorsIngestPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
          </Route>
        ) : null}
        {/* The Vendors hub moved into Relationships; old directory, ingest and source links redirect. */}
        <Route
          path="vendors/*"
          element={<RelocatedPrefixRedirect from="/vendors" to="/crm/vendors" />}
        />
        {debtDestination ? (
          <Route path="credit-facilities">
            <Route
              index
              element={
                <ProtectedLiveDestination destination={debtDestination}>
                  <LazyRoute>
                    <CreditFacilitiesPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path="ledger"
              element={
                <ProtectedLiveDestination destination={debtDestination}>
                  <LazyRoute>
                    <DebtOverviewPage registerOnly />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path=":id"
              element={
                <ProtectedLiveDestination destination={debtDestination}>
                  <LazyRoute>
                    <DebtDetailPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </Route>
        ) : null}
        <Route path="debt/*" element={<LegacyDebtRedirect />} />
        {legalEntitiesDestination ? (
          <Route path="legal-entities">
            <Route
              index
              element={
                <ProtectedLiveDestination destination={legalEntitiesDestination}>
                  <LazyRoute>
                    <LegalEntitiesPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path=":id"
              element={
                <ProtectedLiveDestination destination={legalEntitiesDestination}>
                  <LazyRoute>
                    <LegalEntityDetailPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </Route>
        ) : null}
        {legalOverviewDestination ? (
          <Route
            path="legal"
            element={
              <ProtectedLiveDestination destination={legalOverviewDestination}>
                <LazyRoute>
                  <LegalOverviewPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {legalMattersDestination ? (
          <Route path="legal/matters">
            <Route
              index
              element={
                <ProtectedLiveDestination destination={legalMattersDestination}>
                  <LazyRoute>
                    <LegalMattersPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path=":id"
              element={
                <ProtectedLiveDestination destination={legalMattersDestination}>
                  <LazyRoute>
                    <LegalMatterDetailPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </Route>
        ) : null}
        {legalCounselDestination ? (
          <Route path="legal/counsel">
            <Route
              index
              element={
                <ProtectedLiveDestination destination={legalCounselDestination}>
                  <LazyRoute>
                    <LegalCounselPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path=":id"
              element={
                <ProtectedLiveDestination destination={legalCounselDestination}>
                  <LazyRoute>
                    <LegalCounselDetailPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </Route>
        ) : null}
        {legalInvoicesDestination ? (
          <Route
            path="legal/invoices"
            element={
              <ProtectedLiveDestination destination={legalInvoicesDestination}>
                <LazyRoute>
                  <LegalInvoicesPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {legalCalendarDestination ? (
          <Route
            path="legal/calendar"
            element={
              <ProtectedLiveDestination destination={legalCalendarDestination}>
                <LazyRoute>
                  <LegalCalendarPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {legalRiskDestination ? (
          <Route
            path="legal/risk"
            element={
              <ProtectedLiveDestination destination={legalRiskDestination}>
                <LazyRoute>
                  <LegalRiskPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {legalReportsDestination ? (
          <Route
            path="legal/reports"
            element={
              <ProtectedLiveDestination destination={legalReportsDestination}>
                <LazyRoute>
                  <LegalReportsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {contractsDestination ? (
          <Route path="contracts">
            <Route
              index
              element={
                <ProtectedLiveDestination destination={contractsDestination}>
                  <LazyRoute>
                    <ContractsListPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path=":id"
              element={
                <ProtectedLiveDestination destination={contractsDestination}>
                  <LazyRoute>
                    <ContractDetailPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </Route>
        ) : null}
        {competitorSignalsDestination ? (
          <Route
            path="estimating/competitors/signals"
            element={
              <ProtectedLiveDestination destination={competitorSignalsDestination}>
                <LazyRoute>
                  <CompetitorSignalsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {competitorNewsDestination ? (
          <Route
            path="estimating/competitors/news"
            element={
              <ProtectedLiveDestination destination={competitorNewsDestination}>
                <LazyRoute>
                  <CompetitorNewsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {competitorAlertsDestination ? (
          <Route
            path="estimating/competitors/alerts"
            element={
              <ProtectedLiveDestination destination={competitorAlertsDestination}>
                <LazyRoute>
                  <CompetitorAlertsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {competitorsDestination ? (
          <Route
            path="estimating/competitors"
            element={
              <ProtectedLiveDestination destination={competitorsDestination}>
                <LazyRoute>
                  <CompetitorIntelPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {bidAssemblyDestination ? (
          <Route
            path="bid-assembly"
            element={
              <ProtectedLiveDestination destination={bidAssemblyDestination}>
                <LazyRoute>
                  <BidAssemblyPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {bidCounterpartiesDestination ? (
          <Route
            path="bid-counterparties"
            element={
              <ProtectedLiveDestination destination={bidCounterpartiesDestination}>
                <LazyRoute>
                  <CounterpartyHistoryPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {/* Legacy top-level payroll-hours URL — now the index tab of the Payroll workspace. */}
        <Route path="payroll/exports" element={<Navigate to="/payroll/hours-exports" replace />} />
        {payrollExportsDestination ? (
          <Route
            path="payroll"
            element={
              <ProtectedHubLayout destination={payrollExportsDestination}>
                <LazyRoute>
                  <PayrollWorkspacePage />
                </LazyRoute>
              </ProtectedHubLayout>
            }
          >
            <Route
              index
              element={
                <ProtectedLiveDestination destination={payrollExportsDestination}>
                  <LazyRoute>
                    <PayrollHoursBoardPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path="hours-exports"
              element={
                <ProtectedLiveDestination destination={payrollExportsDestination}>
                  <LazyRoute>
                    <PayrollExportsPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path="exports/:id"
              element={
                <ProtectedLiveDestination destination={payrollExportsDestination}>
                  <LazyRoute>
                    <PayrollExportDetailPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            {payrollCostDestination ? (
              <Route
                path="cost"
                element={
                  <ProtectedLiveDestination destination={payrollCostDestination}>
                    <LazyRoute>
                      <PayrollCostBoardPage />
                    </LazyRoute>
                  </ProtectedLiveDestination>
                }
              />
            ) : null}
            {/*
              P5 prevailing wage. Gated on the permission keys directly rather
              than on the workspace destination: these two arrived from a
              separate sprint and are reachable by URL the moment the API is
              up, independent of the nav registry change that placed the tab.
            */}
            <Route
              path="certified"
              element={
                <ProtectedPermission permission={PERMISSIONS.PAYROLL_CERTIFIED_PAYROLL_READ}>
                  <LazyRoute>
                    <CertifiedPayrollPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="certified/:id"
              element={
                <ProtectedPermission permission={PERMISSIONS.PAYROLL_CERTIFIED_PAYROLL_READ}>
                  <LazyRoute>
                    <CertifiedPayrollDetailPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
            <Route
              path="wage-schedules"
              element={
                <ProtectedPermission permission={PERMISSIONS.PAYROLL_WAGE_SCHEDULES_READ}>
                  <LazyRoute>
                    <WageSchedulesPage />
                  </LazyRoute>
                </ProtectedPermission>
              }
            />
          </Route>
        ) : null}
        {retainageRegisterDestination ? (
          <Route
            path="finance/retainage"
            element={
              <ProtectedLiveDestination destination={retainageRegisterDestination}>
                <LazyRoute>
                  <RetainageRegisterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {fieldTodayDestination ? (
          <Route
            path="field"
            element={
              <ProtectedLiveDestination destination={fieldTodayDestination}>
                <LazyRoute>
                  <FieldTodayPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {fieldDailyReportsDestination ? (
          <Route
            path="field/daily-reports"
            element={
              <ProtectedLiveDestination destination={fieldDailyReportsDestination}>
                <LazyRoute>
                  <DailyReportsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {fieldDailyReportsDestination ? (
          <Route
            path="field/daily-reports/:id"
            element={
              <ProtectedLiveDestination destination={fieldDailyReportsDestination}>
                <LazyRoute>
                  <DailyReportCapturePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {fieldDispatchDestination ? (
          <Route
            path="field/dispatch"
            element={
              <ProtectedLiveDestination destination={fieldDispatchDestination}>
                <LazyRoute>
                  <DispatchBoardPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {fieldObservationsDestination ? (
          <Route
            path="field/observations"
            element={
              <ProtectedLiveDestination destination={fieldObservationsDestination}>
                <LazyRoute>
                  <ObservationsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {fieldTasksDestination ? (
          <Route
            path="field/tasks"
            element={
              <ProtectedLiveDestination destination={fieldTasksDestination}>
                <LazyRoute>
                  <FieldTasksPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {fieldRfisDestination ? (
          <Route
            path="field/rfis"
            element={
              <ProtectedLiveDestination destination={fieldRfisDestination}>
                <LazyRoute>
                  <FieldRfisPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {complianceBoardDestination ? (
          <Route
            path="compliance"
            element={
              <ProtectedLiveDestination destination={complianceBoardDestination}>
                <LazyRoute>
                  <ComplianceBoardPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        <Route path="dashboards/owner" element={<Navigate to="/" replace />} />
        {opsDashboardDestination ? (
          <Route
            path="dashboards/ops"
            element={
              <ProtectedLiveDestination destination={opsDashboardDestination}>
                <LazyRoute>
                  <OpsDashboardPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {portfolioShowcasesDestination ? (
          <>
            <Route
              path="portfolio"
              element={
                <ProtectedLiveDestination destination={portfolioShowcasesDestination}>
                  <LazyRoute>
                    <ShowcaseListPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
            <Route
              path="portfolio/:id"
              element={
                <ProtectedLiveDestination destination={portfolioShowcasesDestination}>
                  <LazyRoute>
                    <ShowcaseBuilderPage />
                  </LazyRoute>
                </ProtectedLiveDestination>
              }
            />
          </>
        ) : null}
        {inboxTriageDestination ? (
          <Route
            path="inbox"
            element={
              <ProtectedLiveDestination destination={inboxTriageDestination}>
                <LazyRoute>
                  <InboxTriagePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {inboxTriageDestination ? (
          <Route
            path="inbox/threads/:id"
            element={
              <ProtectedLiveDestination destination={inboxTriageDestination}>
                <LazyRoute>
                  <ThreadViewPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {digitalTwinDestination ? (
          <Route
            path="digital-twins"
            element={
              <ProtectedLiveDestination destination={digitalTwinDestination}>
                <LazyRoute>
                  <DigitalTwinPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {siteTelemetryDestination ? (
          <Route
            path="site-telemetry"
            element={
              <ProtectedLiveDestination destination={siteTelemetryDestination}>
                <LazyRoute>
                  <SiteTelemetryPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {aiAgentsDestination ? (
          <Route
            path="ai-agents"
            element={
              <ProtectedLiveDestination destination={aiAgentsDestination}>
                <LazyRoute>
                  <AiAgentsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {agentDetailDestination ? (
          <Route
            path="ai-agents/detail"
            element={
              <ProtectedLiveDestination destination={agentDetailDestination}>
                <LazyRoute>
                  <AiAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {financeAgentDetailDestination ? (
          <Route
            path="ai-agents/finance"
            element={
              <ProtectedLiveDestination destination={financeAgentDetailDestination}>
                <LazyRoute>
                  <FinanceAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {schedulingAgentDetailDestination ? (
          <Route
            path="ai-agents/scheduling"
            element={
              <ProtectedLiveDestination destination={schedulingAgentDetailDestination}>
                <LazyRoute>
                  <SchedulingAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {procurementAgentDetailDestination ? (
          <Route
            path="ai-agents/procurement"
            element={
              <ProtectedLiveDestination destination={procurementAgentDetailDestination}>
                <LazyRoute>
                  <ProcurementAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {safetyAgentDetailDestination ? (
          <Route
            path="ai-agents/safety"
            element={
              <ProtectedLiveDestination destination={safetyAgentDetailDestination}>
                <LazyRoute>
                  <SafetyAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {complianceAgentDetailDestination ? (
          <Route
            path="ai-agents/compliance"
            element={
              <ProtectedLiveDestination destination={complianceAgentDetailDestination}>
                <LazyRoute>
                  <ComplianceAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {estimatingAgentDetailDestination ? (
          <Route
            path="ai-agents/estimating"
            element={
              <ProtectedLiveDestination destination={estimatingAgentDetailDestination}>
                <LazyRoute>
                  <EstimatingAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {qaQcAgentDetailDestination ? (
          <Route
            path="ai-agents/qa-qc"
            element={
              <ProtectedLiveDestination destination={qaQcAgentDetailDestination}>
                <LazyRoute>
                  <QaQcAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {hrWorkforceAgentDetailDestination ? (
          <Route
            path="ai-agents/hr-workforce"
            element={
              <ProtectedLiveDestination destination={hrWorkforceAgentDetailDestination}>
                <LazyRoute>
                  <HrWorkforceAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {equipmentAgentDetailDestination ? (
          <Route
            path="ai-agents/equipment"
            element={
              <ProtectedLiveDestination destination={equipmentAgentDetailDestination}>
                <LazyRoute>
                  <EquipmentAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {marketIntelligenceAgentDetailDestination ? (
          <Route
            path="ai-agents/market-intelligence"
            element={
              <ProtectedLiveDestination destination={marketIntelligenceAgentDetailDestination}>
                <LazyRoute>
                  <MarketIntelligenceAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {growthMaAgentDetailDestination ? (
          <Route
            path="ai-agents/growth-ma"
            element={
              <ProtectedLiveDestination destination={growthMaAgentDetailDestination}>
                <LazyRoute>
                  <GrowthMaAgentDetailPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {financeCommandCenterDestination ? (
          <Route
            path="finance"
            element={
              <ProtectedLiveDestination destination={financeCommandCenterDestination}>
                <LazyRoute>
                  <FinanceCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {procurementCommandCenterDestination ? (
          <Route
            path="procurement"
            element={
              <ProtectedLiveDestination destination={procurementCommandCenterDestination}>
                <LazyRoute>
                  <ProcurementCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {schedulingCommandCenterDestination ? (
          <Route
            path="scheduling"
            element={
              <ProtectedLiveDestination destination={schedulingCommandCenterDestination}>
                <LazyRoute>
                  <SchedulingCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {safetyCommandCenterDestination ? (
          <Route
            path="safety"
            element={
              <ProtectedLiveDestination destination={safetyCommandCenterDestination}>
                <LazyRoute>
                  <SafetyCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {qualityCommandCenterDestination ? (
          <Route
            path="quality"
            element={
              <ProtectedLiveDestination destination={qualityCommandCenterDestination}>
                <LazyRoute>
                  <QualityCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {approvalsDestination ? (
          <Route
            path="approvals"
            element={
              <ProtectedLiveDestination destination={approvalsDestination}>
                <LazyRoute>
                  <ApprovalsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {timeAutomationDestination ? (
          <Route
            path="time-automation"
            element={
              <ProtectedLiveDestination destination={timeAutomationDestination}>
                <LazyRoute>
                  <TimeAutomationControlsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {timeAutomationMonitoringDestination ? (
          <Route
            path="time-automation/monitoring"
            element={
              <ProtectedLiveDestination destination={timeAutomationMonitoringDestination}>
                <LazyRoute>
                  <MonitoringNoticePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {timeAutomationProvisioningDestination ? (
          <Route
            path="time-automation/provisioning"
            element={
              <ProtectedLiveDestination destination={timeAutomationProvisioningDestination}>
                <LazyRoute>
                  <ProvisioningSettingsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {timeAutomationFieldTimeDestination ? (
          <Route
            path="time-automation/field-time"
            element={
              <ProtectedLiveDestination destination={timeAutomationFieldTimeDestination}>
                <LazyRoute>
                  <FieldTimePage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {timeAutomationReconciliationDestination ? (
          <Route
            path="time-automation/reconciliation"
            element={
              <ProtectedLiveDestination destination={timeAutomationReconciliationDestination}>
                <LazyRoute>
                  <ClockSharkReconciliationPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {timeAutomationFieldTimeSetupDestination ? (
          <Route
            path="time-automation/field-time-setup"
            element={
              <ProtectedLiveDestination destination={timeAutomationFieldTimeSetupDestination}>
                <LazyRoute>
                  <FieldTimeSetupPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {/* Employee self-service: any signed-in employee reads and acknowledges their own notice. */}
        <Route
          path="my/monitoring-notice"
          element={
            <LazyRoute>
              <MyMonitoringNoticePage />
            </LazyRoute>
          }
        />
        <Route
          path="my/punches"
          element={
            <LazyRoute>
              <MyPunchesPage />
            </LazyRoute>
          }
        />
        {resourcesCommandCenterDestination ? (
          <Route
            path="resources"
            element={
              <ProtectedLiveDestination destination={resourcesCommandCenterDestination}>
                <LazyRoute>
                  <ResourcesCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        <Route
          path="documents/library"
          element={
            <LazyRoute>
              <LegacyDocumentLibraryRedirect />
            </LazyRoute>
          }
        />
        {documentsCommandCenterDestination ? (
          <Route
            path="documents"
            element={
              <ProtectedLiveDestination destination={documentsCommandCenterDestination}>
                <LazyRoute>
                  <DocumentsPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {reportsCommandCenterDestination ? (
          <Route
            path="reports"
            element={
              <ProtectedLiveDestination destination={reportsCommandCenterDestination}>
                <LazyRoute>
                  <ReportingPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {simulationsCommandCenterDestination ? (
          <Route
            path="simulations"
            element={
              <ProtectedLiveDestination destination={simulationsCommandCenterDestination}>
                <LazyRoute>
                  <SimulationsCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        <Route
          path="search"
          element={
            <LazyRoute>
              <LegacySearchRedirect />
            </LazyRoute>
          }
        />
        <Route path="report-exports" element={<Navigate to="/reports?view=exports" replace />} />
        {marketIntelligenceCommandCenterDestination ? (
          <Route
            path="market-intelligence"
            element={
              <ProtectedLiveDestination destination={marketIntelligenceCommandCenterDestination}>
                <LazyRoute>
                  <MarketIntelligenceCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {growthMaCommandCenterDestination ? (
          <Route
            path="growth-ma"
            element={
              <ProtectedLiveDestination destination={growthMaCommandCenterDestination}>
                <LazyRoute>
                  <GrowthMaCommandCenterPage />
                </LazyRoute>
              </ProtectedLiveDestination>
            }
          />
        ) : null}
        {SCAFFOLD_DESTINATIONS.map((destination) => (
          <Route
            key={destination.id}
            path={destination.path.replace(/^\//, '')}
            element={<ProtectedDestination destination={destination} />}
          />
        ))}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
